/**
 * Drawing Overlay — SVG layer with full TradingView-style drawing tools
 *
 * Renders drawings on top of the lightweight-charts canvas via SVG.
 * Supports 16 drawing tools across lines, shapes, Fibonacci, annotations, and measure.
 *
 * Interaction:
 *  - 1-click tools: horizontal, vertical, horizontal-ray, price-label, text
 *  - 2-click tools: trendline, ray, extended-line, rectangle, ellipse, arrow, measure, fibonacci
 *  - 3-click tools: parallel-channel, fib-extension, triangle
 *  - Escape cancels in-progress drawing
 *  - Text tool opens an input field at click position
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { IChartApi, UTCTimestamp } from "lightweight-charts";
import {
  Drawing,
  DrawingTool,
  ChartPoint,
  HorizontalLineDrawing,
  VerticalLineDrawing,
  HorizontalRayDrawing,
  TrendlineDrawing,
  RayDrawing,
  ExtendedLineDrawing,
  FibonacciDrawing,
  FibExtensionDrawing,
  FibFanDrawing,
  FibTimezoneDrawing,
  FibArcDrawing,
  GannFanDrawing,
  PitchforkDrawing,
  RectangleDrawing,
  EllipseDrawing,
  TriangleDrawing,
  ParallelChannelDrawing,
  TextDrawing,
  ArrowDrawing,
  PriceLabelDrawing,
  MeasureDrawing,
  DEFAULT_FIB_LEVELS,
  DEFAULT_FIB_EXT_LEVELS,
  getRequiredClicks,
  getDefaultLineWidth,
} from "../../types/drawing.types";
import "./DrawingOverlay.scss";

// ── Props ─────────────────────────────────────────────────────────
interface DrawingOverlayProps {
  chart: IChartApi | null;
  candleSeries: any;
  drawings: Drawing[];
  activeTool: DrawingTool;
  activeColor: string;
  istOffset: number;
  drawingsVisible: boolean;
  priceSnapTick?: number;
  timeframeSeconds: number;
  onDrawingComplete: (drawing: Drawing) => void;
  onDrawingUpdate: (updated: Drawing) => void;
  onDrawingDelete: (id: string) => void;
  colorTheme: string;
}

// ── Hit-test helpers ──────────────────────────────────────────────
// Larger tolerance on touch screens — fingers are ~44px wide
const isTouchDevice = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
const HIT_TOLERANCE = isTouchDevice ? 18 : 8; // pixels
// Vertical offset applied to touch-drawing preview so the crosshair floats above the finger
const TOUCH_OFFSET_Y = isTouchDevice ? 88 : 0;

const clampTick = (tick?: number): number => {
  if (!tick || !Number.isFinite(tick) || tick <= 0) return 0.01;
  return tick;
};

const tickPrecision = (tick: number): number => {
  const normalized = clampTick(tick);
  const asText = normalized.toString();
  const decimalIdx = asText.indexOf(".");
  if (decimalIdx < 0) return 0;
  return asText.length - decimalIdx - 1;
};

const snapToTick = (value: number, tick: number): number => {
  const normalizedTick = clampTick(tick);
  const snapped = Math.round(value / normalizedTick) * normalizedTick;
  const precision = tickPrecision(normalizedTick);
  return Number(snapped.toFixed(precision));
};

function distToSegment(
  px: number, py: number,
  x1: number, y1: number,
  x2: number, y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

function distToPoint(px: number, py: number, x: number, y: number): number {
  return Math.hypot(px - x, py - y);
}

function pointInRect(
  px: number, py: number,
  x1: number, y1: number,
  x2: number, y2: number,
): boolean {
  const minX = Math.min(x1, x2);
  const maxX = Math.max(x1, x2);
  const minY = Math.min(y1, y2);
  const maxY = Math.max(y1, y2);
  return px >= minX - HIT_TOLERANCE && px <= maxX + HIT_TOLERANCE &&
         py >= minY - HIT_TOLERANCE && py <= maxY + HIT_TOLERANCE;
}

interface DragState {
  drawingId: string;
  startX: number;
  startY: number;
  startPoint: ChartPoint;         // chart coord at mousedown
  originalDrawing: Drawing;       // snapshot before drag
}

interface ResizeDragState {
  drawingId: string;
  anchorIndex: number;            // which anchor handle is being dragged
  originalDrawing: Drawing;       // snapshot before resize
}

interface HorizontalLineEditorState {
  drawingId: string;
  value: string;
  x: number;
  y: number;
  error: string | null;
}

interface TrendlineEditorState {
  drawingId: string;
  time1: string;
  value1: string;
  time2: string;
  value2: string;
  ray: boolean;
  x: number;
  y: number;
  error: string | null;
}

interface FibonacciEditorState {
  drawingId: string;
  time1: string;
  value1: string;
  time2: string;
  value2: string;
  levels: number[];
  selectedLevelIndex: number;
  newLevelValue: string;
  x: number;
  y: number;
  error: string | null;
}

function normalizeFibLevels(levels: number[]): number[] {
  const cleaned = levels
    .filter((v) => Number.isFinite(v))
    .map((v) => Number(v.toFixed(6)));

  const unique = Array.from(new Set(cleaned));
  unique.sort((a, b) => a - b);
  return unique;
}

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

function formatTrendlineTime(unixSec: number, istOffset: number): string {
  const d = new Date((unixSec + istOffset) * 1000);
  const yyyy = d.getUTCFullYear();
  const mm = pad2(d.getUTCMonth() + 1);
  const dd = pad2(d.getUTCDate());
  const hh = pad2(d.getUTCHours());
  const mi = pad2(d.getUTCMinutes());
  return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
}

function parseTrendlineTime(input: string, istOffset: number): number | null {
  const text = input.trim();
  const m = text.match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})\s+(\d{1,2}):(\d{1,2})$/);
  if (!m) return null;

  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const hour = Number(m[4]);
  const minute = Number(m[5]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day) || !Number.isFinite(hour) || !Number.isFinite(minute)) {
    return null;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }

  const utcMs = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  if (!Number.isFinite(utcMs)) return null;
  return Math.floor(utcMs / 1000) - istOffset;
}

// ── Coordinate conversion ─────────────────────────────────────────
function toPixel(
  chart: IChartApi,
  series: any,
  time: number,
  price: number,
  istOffset: number,
): { x: number; y: number } | null {
  try {
    const x = chart.timeScale().timeToCoordinate((time + istOffset) as UTCTimestamp);
    const y = series.priceToCoordinate(price);
    if (x === null || y === null) return null;
    return { x, y };
  } catch {
    return null;
  }
}

function fromPixel(
  chart: IChartApi,
  series: any,
  x: number,
  y: number,
  istOffset: number,
): ChartPoint | null {
  try {
    const time = chart.timeScale().coordinateToTime(x);
    const price = series.coordinateToPrice(y);
    if (time === null || price === null) return null;
    const timeNum = typeof time === "number" ? time : 0;
    return { time: timeNum - istOffset, price };
  } catch {
    return null;
  }
}

// ── Ray/line extension helpers ────────────────────────────────────
function extendRay(
  p1: { x: number; y: number },
  p2: { x: number; y: number },
): { x: number; y: number } {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) return p2;
  return { x: p1.x + (dx / len) * 10000, y: p1.y + (dy / len) * 10000 };
}

function extendBothWays(
  p1: { x: number; y: number },
  p2: { x: number; y: number },
): { start: { x: number; y: number }; end: { x: number; y: number } } {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) return { start: p1, end: p2 };
  const ux = dx / len;
  const uy = dy / len;
  return {
    start: { x: p1.x - ux * 10000, y: p1.y - uy * 10000 },
    end: { x: p2.x + ux * 10000, y: p2.y + uy * 10000 },
  };
}

// Alias: extend from p1 through p2 outward (same as extendRay)
function extendInOneDirection(
  p1: { x: number; y: number },
  p2: { x: number; y: number },
): { x: number; y: number } {
  return extendRay(p1, p2);
}

// ── Drawing ID generator ──────────────────────────────────────────
let drawingIdCounter = 0;
function nextDrawingId(): string {
  return `drawing_${Date.now()}_${++drawingIdCounter}`;
}

// ═══════════════════════════════════════════════════════════════════
const DrawingOverlay: React.FC<DrawingOverlayProps> = ({
  chart,
  candleSeries,
  drawings,
  activeTool,
  activeColor,
  istOffset,
  drawingsVisible,
  priceSnapTick = 0.05,
  timeframeSeconds,
  onDrawingComplete,
  onDrawingUpdate,
  onDrawingDelete,
  colorTheme,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });
  const [pendingPoints, setPendingPoints] = useState<ChartPoint[]>([]);
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(null);
  const [, setRenderTick] = useState(0);

  // Text tool input state
  const [textInput, setTextInput] = useState<{
    x: number; y: number; time: number; price: number;
  } | null>(null);
  const [textInputValue, setTextInputValue] = useState("");
  const textInputRef = useRef<HTMLInputElement>(null);

  // ── Selection & drag state ──────────────────────────────────
  const [selectedDrawingId, setSelectedDrawingId] = useState<string | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // ── Resize drag state ───────────────────────────────────────
  const resizeDragRef = useRef<ResizeDragState | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [lineEditor, setLineEditor] = useState<HorizontalLineEditorState | null>(null);
  const [trendlineEditor, setTrendlineEditor] = useState<TrendlineEditorState | null>(null);
  const [fibonacciEditor, setFibonacciEditor] = useState<FibonacciEditorState | null>(null);
  const lineEditorRef = useRef<HTMLDivElement>(null);
  const trendlineEditorRef = useRef<HTMLDivElement>(null);
  const fibonacciEditorRef = useRef<HTMLDivElement>(null);
  const normalizedTick = clampTick(priceSnapTick);
  const lineValuePrecision = tickPrecision(normalizedTick);
  const lineInputStep = normalizedTick.toString();

  // ── Resize observer ─────────────────────────────────────────
  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    const ro = new ResizeObserver(() => {
      setSvgSize({ width: el.clientWidth, height: el.clientHeight });
    });
    ro.observe(el);
    setSvgSize({ width: el.clientWidth, height: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  // ── Re-render on chart scroll/zoom ──────────────────────────
  useEffect(() => {
    if (!chart) return;
    const handler = () => setRenderTick(t => t + 1);
    const ts = chart.timeScale();
    ts.subscribeVisibleTimeRangeChange(handler);
    ts.subscribeVisibleLogicalRangeChange(handler);
    ts.subscribeSizeChange(handler);
    // Keep overlay geometry in sync with candle rescale/scroll interactions.
    const crosshairHandler = () => setRenderTick(t => t + 1);
    chart.subscribeCrosshairMove(crosshairHandler);
    return () => {
      try {
        ts.unsubscribeVisibleTimeRangeChange(handler);
        ts.unsubscribeVisibleLogicalRangeChange(handler);
        ts.unsubscribeSizeChange(handler);
        chart.unsubscribeCrosshairMove(crosshairHandler);
      } catch (_) {}
    };
  }, [chart]);

  // ── Escape / Delete key handlers ────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setPendingPoints([]);
        setCursorPos(null);
        setTextInput(null);
        setTextInputValue("");
        setSelectedDrawingId(null);
        dragRef.current = null;
        setIsDragging(false);
        resizeDragRef.current = null;
        setIsResizing(false);
        setLineEditor(null);
        setTrendlineEditor(null);
        setFibonacciEditor(null);
      }
      // Delete / Backspace removes selected drawing
      if ((e.key === "Delete" || e.key === "Backspace") && selectedDrawingId) {
        // Don't delete if user is typing in an input
        if (document.activeElement?.tagName === "INPUT" || document.activeElement?.tagName === "TEXTAREA") return;
        e.preventDefault();
        onDrawingDelete(selectedDrawingId);
        setSelectedDrawingId(null);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedDrawingId, onDrawingDelete]);

  // Auto-focus text input when it appears
  useEffect(() => {
    if (textInput && textInputRef.current) {
      textInputRef.current.focus();
    }
  }, [textInput]);

  // Clear pending points when tool changes
  useEffect(() => {
    setPendingPoints([]);
    setCursorPos(null);
    setTextInput(null);
    setTextInputValue("");
    setSelectedDrawingId(null);
    dragRef.current = null;
    setIsDragging(false);
    resizeDragRef.current = null;
    setIsResizing(false);
    setLineEditor(null);
    setTrendlineEditor(null);
    setFibonacciEditor(null);
  }, [activeTool]);

  // Close the horizontal-line editor if selection changes or line gets removed.
  useEffect(() => {
    if (!lineEditor) return;

    if (!selectedDrawingId || selectedDrawingId !== lineEditor.drawingId) {
      setLineEditor(null);
      return;
    }

    const target = drawings.find((dr) => dr.id === lineEditor.drawingId);
    if (!target || target.tool !== "horizontal") {
      setLineEditor(null);
    }
  }, [lineEditor, selectedDrawingId, drawings]);

  useEffect(() => {
    if (!trendlineEditor) return;

    if (!selectedDrawingId || selectedDrawingId !== trendlineEditor.drawingId) {
      setTrendlineEditor(null);
      return;
    }

    const target = drawings.find((dr) => dr.id === trendlineEditor.drawingId);
    if (!target || target.tool !== "trendline") {
      setTrendlineEditor(null);
    }
  }, [trendlineEditor, selectedDrawingId, drawings]);

  useEffect(() => {
    if (!fibonacciEditor) return;

    if (!selectedDrawingId || selectedDrawingId !== fibonacciEditor.drawingId) {
      setFibonacciEditor(null);
      return;
    }

    const target = drawings.find((dr) => dr.id === fibonacciEditor.drawingId);
    if (!target || target.tool !== "fibonacci") {
      setFibonacciEditor(null);
    }
  }, [fibonacciEditor, selectedDrawingId, drawings]);

  // Click outside to close the editor popover.
  useEffect(() => {
    if (!lineEditor) return;
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (lineEditorRef.current?.contains(target)) return;
      if (target.closest(".line-settings-btn")) return;
      setLineEditor(null);
    };
    window.addEventListener("mousedown", handleOutside);
    return () => window.removeEventListener("mousedown", handleOutside);
  }, [lineEditor]);

  useEffect(() => {
    if (!trendlineEditor) return;
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (trendlineEditorRef.current?.contains(target)) return;
      if (target.closest(".trendline-settings-btn")) return;
      setTrendlineEditor(null);
    };
    window.addEventListener("mousedown", handleOutside);
    return () => window.removeEventListener("mousedown", handleOutside);
  }, [trendlineEditor]);

  useEffect(() => {
    if (!fibonacciEditor) return;
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (fibonacciEditorRef.current?.contains(target)) return;
      if (target.closest(".fibonacci-settings-btn")) return;
      setFibonacciEditor(null);
    };
    window.addEventListener("mousedown", handleOutside);
    return () => window.removeEventListener("mousedown", handleOutside);
  }, [fibonacciEditor]);

  const openHorizontalLineEditor = useCallback(
    (line: HorizontalLineDrawing, x: number, y: number) => {
      const popupWidth = 210;
      const popupHeight = 98;
      const margin = 8;

      const desiredX = x - popupWidth + 20;
      const desiredY = y + 14;

      const safeX = Math.min(
        Math.max(margin, desiredX),
        Math.max(margin, svgSize.width - popupWidth - margin)
      );
      const safeY = Math.min(
        Math.max(margin, desiredY),
        Math.max(margin, svgSize.height - popupHeight - margin)
      );

      setLineEditor({
        drawingId: line.id,
        value: line.price.toFixed(lineValuePrecision),
        x: safeX,
        y: safeY,
        error: null,
      });
    },
    [svgSize, lineValuePrecision]
  );

  const submitHorizontalLineEditor = useCallback(() => {
    if (!lineEditor) return;
    const raw = lineEditor.value.trim();
    if (!raw) {
      setLineEditor((prev) =>
        prev ? { ...prev, error: "Enter a valid numeric price" } : prev
      );
      return;
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) {
      setLineEditor((prev) =>
        prev ? { ...prev, error: "Enter a valid numeric price" } : prev
      );
      return;
    }

    const target = drawings.find((dr) => dr.id === lineEditor.drawingId);
    if (!target || target.tool !== "horizontal") {
      setLineEditor(null);
      return;
    }

    const updated: HorizontalLineDrawing = {
      ...(target as HorizontalLineDrawing),
      price: snapToTick(parsed, normalizedTick),
    };
    onDrawingUpdate(updated);
    setLineEditor(null);
  }, [lineEditor, drawings, onDrawingUpdate, normalizedTick]);

  const openTrendlineEditor = useCallback(
    (line: TrendlineDrawing, x: number, y: number) => {
      const margin = 8;
      const isCompact = svgSize.width <= 480;
      const popupWidth = isCompact ? Math.max(240, svgSize.width - margin * 2) : 355;
      const popupHeight = isCompact ? Math.min(svgSize.height - margin * 2, 360) : 266;

      const desiredX = isCompact ? margin : x - popupWidth + 20;
      const desiredY = isCompact ? margin : y + 14;

      const safeX = Math.min(
        Math.max(margin, desiredX),
        Math.max(margin, svgSize.width - popupWidth - margin)
      );
      const safeY = Math.min(
        Math.max(margin, desiredY),
        Math.max(margin, svgSize.height - popupHeight - margin)
      );

      setTrendlineEditor({
        drawingId: line.id,
        time1: formatTrendlineTime(line.point1.time, istOffset),
        value1: line.point1.price.toFixed(lineValuePrecision),
        time2: formatTrendlineTime(line.point2.time, istOffset),
        value2: line.point2.price.toFixed(lineValuePrecision),
        ray: line.ray ?? true,
        x: safeX,
        y: safeY,
        error: null,
      });
    },
    [svgSize, istOffset, lineValuePrecision]
  );

  const submitTrendlineEditor = useCallback(() => {
    if (!trendlineEditor) return;

    const rawV1 = trendlineEditor.value1.trim();
    const rawV2 = trendlineEditor.value2.trim();
    if (!rawV1 || !rawV2) {
      setTrendlineEditor((prev) =>
        prev ? { ...prev, error: "Both point values are required" } : prev
      );
      return;
    }

    const parsedV1 = Number(rawV1);
    const parsedV2 = Number(rawV2);
    if (!Number.isFinite(parsedV1) || !Number.isFinite(parsedV2)) {
      setTrendlineEditor((prev) =>
        prev ? { ...prev, error: "Enter valid numeric values for both points" } : prev
      );
      return;
    }

    const parsedT1 = parseTrendlineTime(trendlineEditor.time1, istOffset);
    const parsedT2 = parseTrendlineTime(trendlineEditor.time2, istOffset);
    if (parsedT1 == null || parsedT2 == null) {
      setTrendlineEditor((prev) =>
        prev ? { ...prev, error: "Use date-time format YYYY.MM.DD HH:mm" } : prev
      );
      return;
    }

    const target = drawings.find((dr) => dr.id === trendlineEditor.drawingId);
    if (!target || target.tool !== "trendline") {
      setTrendlineEditor(null);
      return;
    }

    const normalizedV1 = Number(parsedV1.toFixed(lineValuePrecision));
    const normalizedV2 = Number(parsedV2.toFixed(lineValuePrecision));

    const updated: TrendlineDrawing = {
      ...(target as TrendlineDrawing),
      point1: {
        time: parsedT1,
        price: normalizedV1,
      },
      point2: {
        time: parsedT2,
        price: normalizedV2,
      },
      ray: Boolean(trendlineEditor.ray),
    };
    onDrawingUpdate(updated);
    setTrendlineEditor(null);
  }, [trendlineEditor, drawings, onDrawingUpdate, istOffset, lineValuePrecision]);

  const handleTrendlineEditorKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitTrendlineEditor();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setTrendlineEditor(null);
    }
  }, [submitTrendlineEditor]);

  const openFibonacciEditor = useCallback(
    (fib: FibonacciDrawing, x: number, y: number) => {
      const margin = 8;
      const isCompact = svgSize.width <= 480;
      const popupWidth = isCompact ? Math.max(280, svgSize.width - margin * 2) : 430;
      const popupHeight = isCompact ? Math.min(svgSize.height - margin * 2, 480) : 360;

      const desiredX = isCompact ? margin : x - popupWidth + 20;
      const desiredY = isCompact ? margin : y + 14;

      const safeX = Math.min(
        Math.max(margin, desiredX),
        Math.max(margin, svgSize.width - popupWidth - margin)
      );
      const safeY = Math.min(
        Math.max(margin, desiredY),
        Math.max(margin, svgSize.height - popupHeight - margin)
      );

      const levels = normalizeFibLevels(Array.isArray(fib.levels) && fib.levels.length ? fib.levels : DEFAULT_FIB_LEVELS);
      setFibonacciEditor({
        drawingId: fib.id,
        time1: formatTrendlineTime(fib.point1.time, istOffset),
        value1: fib.point1.price.toFixed(lineValuePrecision),
        time2: formatTrendlineTime(fib.point2.time, istOffset),
        value2: fib.point2.price.toFixed(lineValuePrecision),
        levels,
        selectedLevelIndex: levels.length > 0 ? 0 : -1,
        newLevelValue: "",
        x: safeX,
        y: safeY,
        error: null,
      });
    },
    [svgSize, istOffset, lineValuePrecision]
  );

  const addFibonacciLevel = useCallback(() => {
    if (!fibonacciEditor) return;
    const raw = fibonacciEditor.newLevelValue.trim();
    if (!raw) {
      setFibonacciEditor((prev) => prev ? { ...prev, error: "Enter a valid level value" } : prev);
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) {
      setFibonacciEditor((prev) => prev ? { ...prev, error: "Enter a valid level value" } : prev);
      return;
    }

    const levels = normalizeFibLevels([...fibonacciEditor.levels, parsed]);
    const idx = levels.findIndex((v) => Math.abs(v - Number(parsed.toFixed(6))) < 1e-9);
    setFibonacciEditor((prev) => prev ? {
      ...prev,
      levels,
      selectedLevelIndex: idx >= 0 ? idx : levels.length - 1,
      newLevelValue: "",
      error: null,
    } : prev);
  }, [fibonacciEditor]);

  const deleteFibonacciLevel = useCallback(() => {
    if (!fibonacciEditor) return;
    const idx = fibonacciEditor.selectedLevelIndex;
    if (idx < 0 || idx >= fibonacciEditor.levels.length) {
      setFibonacciEditor((prev) => prev ? { ...prev, error: "Select a level to delete" } : prev);
      return;
    }

    const levels = fibonacciEditor.levels.filter((_, i) => i !== idx);
    setFibonacciEditor((prev) => prev ? {
      ...prev,
      levels,
      selectedLevelIndex: levels.length === 0 ? -1 : Math.max(0, Math.min(idx, levels.length - 1)),
      error: null,
    } : prev);
  }, [fibonacciEditor]);

  const resetFibonacciLevels = useCallback(() => {
    const defaults = normalizeFibLevels([...DEFAULT_FIB_LEVELS]);
    setFibonacciEditor((prev) => prev ? {
      ...prev,
      levels: defaults,
      selectedLevelIndex: defaults.length > 0 ? 0 : -1,
      newLevelValue: "",
      error: null,
    } : prev);
  }, []);

  const submitFibonacciEditor = useCallback(() => {
    if (!fibonacciEditor) return;

    const rawV1 = fibonacciEditor.value1.trim();
    const rawV2 = fibonacciEditor.value2.trim();
    if (!rawV1 || !rawV2) {
      setFibonacciEditor((prev) => prev ? { ...prev, error: "Both point values are required" } : prev);
      return;
    }

    const parsedV1 = Number(rawV1);
    const parsedV2 = Number(rawV2);
    if (!Number.isFinite(parsedV1) || !Number.isFinite(parsedV2)) {
      setFibonacciEditor((prev) => prev ? { ...prev, error: "Enter valid numeric values for both points" } : prev);
      return;
    }

    const parsedT1 = parseTrendlineTime(fibonacciEditor.time1, istOffset);
    const parsedT2 = parseTrendlineTime(fibonacciEditor.time2, istOffset);
    if (parsedT1 == null || parsedT2 == null) {
      setFibonacciEditor((prev) => prev ? { ...prev, error: "Use date-time format YYYY.MM.DD HH:mm" } : prev);
      return;
    }

    const levels = normalizeFibLevels(fibonacciEditor.levels);
    if (levels.length === 0) {
      setFibonacciEditor((prev) => prev ? { ...prev, error: "At least one Fibonacci level is required" } : prev);
      return;
    }

    const target = drawings.find((dr) => dr.id === fibonacciEditor.drawingId);
    if (!target || target.tool !== "fibonacci") {
      setFibonacciEditor(null);
      return;
    }

    const updated: FibonacciDrawing = {
      ...(target as FibonacciDrawing),
      point1: {
        time: parsedT1,
        price: Number(parsedV1.toFixed(lineValuePrecision)),
      },
      point2: {
        time: parsedT2,
        price: Number(parsedV2.toFixed(lineValuePrecision)),
      },
      levels,
    };

    onDrawingUpdate(updated);
    setFibonacciEditor(null);
  }, [fibonacciEditor, drawings, onDrawingUpdate, istOffset, lineValuePrecision]);

  const handleFibonacciEditorKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setFibonacciEditor(null);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      submitFibonacciEditor();
    }
  }, [submitFibonacciEditor]);

  // ── Hit-test: find drawing under a pixel position ───────────
  const hitTestDrawing = useCallback(
    (px: number, py: number): Drawing | null => {
      if (!chart || !candleSeries || !drawingsVisible) return null;
      // Iterate in reverse so topmost drawing is selected first
      for (let i = drawings.length - 1; i >= 0; i--) {
        const d = drawings[i];
        if (!d.visible) continue;

        switch (d.tool) {
          case "horizontal": {
            const hd = d as HorizontalLineDrawing;
            const y = candleSeries.priceToCoordinate(hd.price);
            if (y !== null && Math.abs(py - y) <= HIT_TOLERANCE) return d;
            break;
          }
          case "vertical": {
            const vd = d as VerticalLineDrawing;
            const x = chart.timeScale().timeToCoordinate((vd.time + istOffset) as UTCTimestamp);
            if (x !== null && Math.abs(px - x) <= HIT_TOLERANCE) return d;
            break;
          }
          case "horizontal-ray": {
            const hrd = d as HorizontalRayDrawing;
            const p = toPixel(chart, candleSeries, hrd.point.time, hrd.point.price, istOffset);
            if (p && Math.abs(py - p.y) <= HIT_TOLERANCE && px >= p.x - HIT_TOLERANCE) return d;
            break;
          }
          case "trendline": {
            const td = d as TrendlineDrawing;
            const p1 = toPixel(chart, candleSeries, td.point1.time, td.point1.price, istOffset);
            const p2 = toPixel(chart, candleSeries, td.point2.time, td.point2.price, istOffset);
            if (p1 && p2) {
              if (td.ray ?? true) {
                const ep = extendRay(p1, p2);
                if (distToSegment(px, py, p1.x, p1.y, ep.x, ep.y) <= HIT_TOLERANCE) return d;
              } else if (distToSegment(px, py, p1.x, p1.y, p2.x, p2.y) <= HIT_TOLERANCE) {
                return d;
              }
            }
            break;
          }
          case "ray": {
            const rd = d as RayDrawing;
            const p1 = toPixel(chart, candleSeries, rd.point1.time, rd.point1.price, istOffset);
            const p2 = toPixel(chart, candleSeries, rd.point2.time, rd.point2.price, istOffset);
            if (p1 && p2) {
              const ep = extendRay(p1, p2);
              if (distToSegment(px, py, p1.x, p1.y, ep.x, ep.y) <= HIT_TOLERANCE) return d;
            }
            break;
          }
          case "extended-line": {
            const eld = d as ExtendedLineDrawing;
            const p1 = toPixel(chart, candleSeries, eld.point1.time, eld.point1.price, istOffset);
            const p2 = toPixel(chart, candleSeries, eld.point2.time, eld.point2.price, istOffset);
            if (p1 && p2) {
              const ext = extendBothWays(p1, p2);
              if (distToSegment(px, py, ext.start.x, ext.start.y, ext.end.x, ext.end.y) <= HIT_TOLERANCE) return d;
            }
            break;
          }
          case "fibonacci": {
            const fd = d as FibonacciDrawing;
            const p1 = toPixel(chart, candleSeries, fd.point1.time, fd.point1.price, istOffset);
            const p2 = toPixel(chart, candleSeries, fd.point2.time, fd.point2.price, istOffset);
            if (p1 && p2) {
              // Hit if near any fib level line within the price range
              for (const level of fd.levels) {
                const price = fd.point2.price + (fd.point1.price - fd.point2.price) * level;
                const y = candleSeries.priceToCoordinate(price);
                if (y !== null && Math.abs(py - y) <= HIT_TOLERANCE) return d;
              }
            }
            break;
          }
          case "fib-extension": {
            const fed = d as FibExtensionDrawing;
            const p1 = toPixel(chart, candleSeries, fed.point1.time, fed.point1.price, istOffset);
            const p2 = toPixel(chart, candleSeries, fed.point2.time, fed.point2.price, istOffset);
            const p3 = toPixel(chart, candleSeries, fed.point3.time, fed.point3.price, istOffset);
            if (p1 && p2 && p3) {
              if (distToSegment(px, py, p1.x, p1.y, p2.x, p2.y) <= HIT_TOLERANCE) return d;
              if (distToSegment(px, py, p2.x, p2.y, p3.x, p3.y) <= HIT_TOLERANCE) return d;
            }
            break;
          }
          case "rectangle": {
            const rd = d as RectangleDrawing;
            const p1 = toPixel(chart, candleSeries, rd.point1.time, rd.point1.price, istOffset);
            const p2 = toPixel(chart, candleSeries, rd.point2.time, rd.point2.price, istOffset);
            if (p1 && p2 && pointInRect(px, py, p1.x, p1.y, p2.x, p2.y)) return d;
            break;
          }
          case "ellipse": {
            const ed = d as EllipseDrawing;
            const p1 = toPixel(chart, candleSeries, ed.point1.time, ed.point1.price, istOffset);
            const p2 = toPixel(chart, candleSeries, ed.point2.time, ed.point2.price, istOffset);
            if (p1 && p2) {
              const cx = (p1.x + p2.x) / 2;
              const cy = (p1.y + p2.y) / 2;
              const rx = Math.abs(p2.x - p1.x) / 2 || 1;
              const ry = Math.abs(p2.y - p1.y) / 2 || 1;
              const norm = ((px - cx) / rx) ** 2 + ((py - cy) / ry) ** 2;
              if (norm <= 1.3) return d; // inside or near edge
            }
            break;
          }
          case "triangle": {
            const td = d as TriangleDrawing;
            const p1 = toPixel(chart, candleSeries, td.point1.time, td.point1.price, istOffset);
            const p2 = toPixel(chart, candleSeries, td.point2.time, td.point2.price, istOffset);
            const p3 = toPixel(chart, candleSeries, td.point3.time, td.point3.price, istOffset);
            if (p1 && p2 && p3) {
              if (distToSegment(px, py, p1.x, p1.y, p2.x, p2.y) <= HIT_TOLERANCE) return d;
              if (distToSegment(px, py, p2.x, p2.y, p3.x, p3.y) <= HIT_TOLERANCE) return d;
              if (distToSegment(px, py, p3.x, p3.y, p1.x, p1.y) <= HIT_TOLERANCE) return d;
            }
            break;
          }
          case "parallel-channel": {
            const pcd = d as ParallelChannelDrawing;
            const p1 = toPixel(chart, candleSeries, pcd.point1.time, pcd.point1.price, istOffset);
            const p2 = toPixel(chart, candleSeries, pcd.point2.time, pcd.point2.price, istOffset);
            const p3 = toPixel(chart, candleSeries, pcd.point3.time, pcd.point3.price, istOffset);
            if (p1 && p2 && p3) {
              if (distToSegment(px, py, p1.x, p1.y, p2.x, p2.y) <= HIT_TOLERANCE) return d;
              const dx = p2.x - p1.x;
              const dy = p2.y - p1.y;
              const len = Math.sqrt(dx * dx + dy * dy);
              if (len > 0) {
                const nx = -dy / len;
                const ny = dx / len;
                const dist = (p3.x - p1.x) * nx + (p3.y - p1.y) * ny;
                const q1 = { x: p1.x + nx * dist, y: p1.y + ny * dist };
                const q2 = { x: p2.x + nx * dist, y: p2.y + ny * dist };
                if (distToSegment(px, py, q1.x, q1.y, q2.x, q2.y) <= HIT_TOLERANCE) return d;
              }
            }
            break;
          }
          case "text": {
            const txtd = d as TextDrawing;
            const p = toPixel(chart, candleSeries, txtd.point.time, txtd.point.price, istOffset);
            if (p && distToPoint(px, py, p.x, p.y) <= 30) return d;
            break;
          }
          case "arrow": {
            const ad = d as ArrowDrawing;
            const p1 = toPixel(chart, candleSeries, ad.point1.time, ad.point1.price, istOffset);
            const p2 = toPixel(chart, candleSeries, ad.point2.time, ad.point2.price, istOffset);
            if (p1 && p2 && distToSegment(px, py, p1.x, p1.y, p2.x, p2.y) <= HIT_TOLERANCE) return d;
            break;
          }
          case "price-label": {
            const pld = d as PriceLabelDrawing;
            const p = toPixel(chart, candleSeries, pld.point.time, pld.point.price, istOffset);
            if (p && distToPoint(px, py, p.x, p.y) <= 20) return d;
            break;
          }
          case "measure": {
            const md = d as MeasureDrawing;
            const p1 = toPixel(chart, candleSeries, md.point1.time, md.point1.price, istOffset);
            const p2 = toPixel(chart, candleSeries, md.point2.time, md.point2.price, istOffset);
            if (p1 && p2 && pointInRect(px, py, p1.x, p1.y, p2.x, p2.y)) return d;
            break;
          }
          case "fib-fan": case "gann-fan": {
            const ff = d as FibFanDrawing;
            const p1 = toPixel(chart, candleSeries, ff.point1.time, ff.point1.price, istOffset);
            const p2 = toPixel(chart, candleSeries, ff.point2.time, ff.point2.price, istOffset);
            if (p1 && p2) {
              // Hit if near origin point
              if (distToPoint(px, py, p1.x, p1.y) <= HIT_TOLERANCE * 2) return d;
              // Hit if near any of the fan lines
              const fibLevels = d.tool === "fib-fan" ? [0.236, 0.382, 0.5, 0.618, 0.786, 1] : [1/8, 1/4, 1/3, 1/2, 1, 2, 3, 4, 8];
              for (const level of fibLevels) {
                const targetX = p1.x + (p2.x - p1.x);
                const targetY = d.tool === "fib-fan" ? p1.y + (p2.y - p1.y) * level : p1.y + (p2.y - p1.y);
                if (distToSegment(px, py, p1.x, p1.y, targetX, targetY) <= HIT_TOLERANCE) return d;
              }
            }
            break;
          }
          case "fib-timezone": {
            const ftz = d as FibTimezoneDrawing;
            const p1 = toPixel(chart, candleSeries, ftz.point1.time, ftz.point1.price, istOffset);
            const p2 = toPixel(chart, candleSeries, ftz.point2.time, ftz.point2.price, istOffset);
            if (p1 && p2) {
              const timeDiff = ftz.point2.time - ftz.point1.time;
              const fibSeq = [0, 1, 2, 3, 5, 8, 13, 21, 34];
              for (const fib of fibSeq) {
                const time = ftz.point1.time + timeDiff * fib;
                const x = chart.timeScale().timeToCoordinate((time + istOffset) as UTCTimestamp);
                if (x !== null && Math.abs(px - x) <= HIT_TOLERANCE) return d;
              }
            }
            break;
          }
          case "fib-arc": {
            const fa = d as FibArcDrawing;
            const p1 = toPixel(chart, candleSeries, fa.point1.time, fa.point1.price, istOffset);
            const p2 = toPixel(chart, candleSeries, fa.point2.time, fa.point2.price, istOffset);
            if (p1 && p2) {
              // Hit if near center point or any arc
              if (distToPoint(px, py, p1.x, p1.y) <= HIT_TOLERANCE * 2) return d;
              const priceDiff = Math.abs(fa.point1.price - fa.point2.price);
              const fibLevels = [0.236, 0.382, 0.5, 0.618, 0.786, 1];
              for (const level of fibLevels) {
                const radius = priceDiff * level;
                const radiusPixels = Math.abs(candleSeries.priceToCoordinate(fa.point1.price)! - candleSeries.priceToCoordinate(fa.point1.price - radius)!);
                const distFromCenter = Math.hypot(px - p1.x, py - p1.y);
                if (Math.abs(distFromCenter - radiusPixels) <= HIT_TOLERANCE) return d;
              }
            }
            break;
          }
          case "pitchfork": {
            const pf = d as PitchforkDrawing;
            const p1 = toPixel(chart, candleSeries, pf.point1.time, pf.point1.price, istOffset);
            const p2 = toPixel(chart, candleSeries, pf.point2.time, pf.point2.price, istOffset);
            const p3 = toPixel(chart, candleSeries, pf.point3.time, pf.point3.price, istOffset);
            if (p1 && p2 && p3) {
              // Median line
              const midX = (p2.x + p3.x) / 2;
              const midY = (p2.y + p3.y) / 2;
              if (distToSegment(px, py, p1.x, p1.y, midX, midY) <= HIT_TOLERANCE) return d;
              // Upper and lower lines
              if (distToSegment(px, py, p2.x, p2.y, p2.x + 1000, p2.y) <= HIT_TOLERANCE) return d;
              if (distToSegment(px, py, p3.x, p3.y, p3.x + 1000, p3.y) <= HIT_TOLERANCE) return d;
            }
            break;
          }
        }
      }
      return null;
    },
    [chart, candleSeries, drawings, drawingsVisible, istOffset],
  );

  // ── Get anchor points of a drawing for rendering selection handles ──
  const getDrawingAnchors = useCallback(
    (d: Drawing): { x: number; y: number }[] => {
      if (!chart || !candleSeries) return [];
      const anchors: { x: number; y: number }[] = [];
      const tp = (time: number, price: number) => toPixel(chart, candleSeries, time, price, istOffset);

      switch (d.tool) {
        case "horizontal": {
          const hd = d as HorizontalLineDrawing;
          const y = candleSeries.priceToCoordinate(hd.price);
          if (y !== null) { anchors.push({ x: 40, y }, { x: svgSize.width / 2, y }); }
          break;
        }
        case "vertical": {
          const vd = d as VerticalLineDrawing;
          const x = chart.timeScale().timeToCoordinate((vd.time + istOffset) as UTCTimestamp);
          if (x !== null) { anchors.push({ x, y: 40 }, { x, y: svgSize.height / 2 }); }
          break;
        }
        case "horizontal-ray": {
          const hrd = d as HorizontalRayDrawing;
          const p = tp(hrd.point.time, hrd.point.price);
          if (p) anchors.push(p);
          break;
        }
        case "trendline": case "ray": case "extended-line": case "arrow": case "measure": {
          const td = d as TrendlineDrawing;
          const p1 = tp(td.point1.time, td.point1.price);
          const p2 = tp(td.point2.time, td.point2.price);
          if (p1) anchors.push(p1);
          if (p2) anchors.push(p2);
          break;
        }
        case "fibonacci": {
          const fd = d as FibonacciDrawing;
          const p1 = tp(fd.point1.time, fd.point1.price);
          const p2 = tp(fd.point2.time, fd.point2.price);
          if (p1) anchors.push(p1);
          if (p2) anchors.push(p2);
          break;
        }
        case "rectangle": case "ellipse": {
          const rd = d as RectangleDrawing;
          const p1 = tp(rd.point1.time, rd.point1.price);
          const p2 = tp(rd.point2.time, rd.point2.price);
          if (p1) anchors.push(p1);
          if (p2) anchors.push(p2);
          break;
        }
        case "parallel-channel": case "triangle": case "fib-extension": case "pitchfork": {
          const pcd = d as ParallelChannelDrawing;
          const p1 = tp(pcd.point1.time, pcd.point1.price);
          const p2 = tp(pcd.point2.time, pcd.point2.price);
          const p3 = tp(pcd.point3.time, pcd.point3.price);
          if (p1) anchors.push(p1);
          if (p2) anchors.push(p2);
          if (p3) anchors.push(p3);
          break;
        }
        case "fib-fan": case "fib-timezone": case "fib-arc": case "gann-fan": {
          const ff = d as FibFanDrawing;
          const p1 = tp(ff.point1.time, ff.point1.price);
          const p2 = tp(ff.point2.time, ff.point2.price);
          if (p1) anchors.push(p1);
          if (p2) anchors.push(p2);
          break;
        }
        case "text": {
          const txtd = d as TextDrawing;
          const p = tp(txtd.point.time, txtd.point.price);
          if (p) anchors.push(p);
          break;
        }
        case "price-label": {
          const pld = d as PriceLabelDrawing;
          const p = tp(pld.point.time, pld.point.price);
          if (p) anchors.push(p);
          break;
        }
      }
      return anchors;
    },
    [chart, candleSeries, istOffset, svgSize],
  );

  // ── Apply a (dTime, dPrice) offset to all points of a drawing ──
  const offsetDrawing = useCallback(
    (d: Drawing, dTime: number, dPrice: number): Drawing => {
      const shift = (pt: ChartPoint): ChartPoint => ({ time: pt.time + dTime, price: pt.price + dPrice });
      switch (d.tool) {
        case "horizontal":
          return { ...d, price: (d as HorizontalLineDrawing).price + dPrice } as Drawing;
        case "vertical":
          return { ...d, time: (d as VerticalLineDrawing).time + dTime } as Drawing;
        case "horizontal-ray":
        case "price-label":
        case "text":
          return { ...d, point: shift((d as HorizontalRayDrawing).point) } as Drawing;
        case "trendline": case "ray": case "extended-line":
        case "rectangle": case "ellipse": case "arrow": case "measure":
        case "fibonacci": case "fib-fan": case "fib-timezone": case "fib-arc": case "gann-fan":
          return { ...d, point1: shift((d as TrendlineDrawing).point1), point2: shift((d as TrendlineDrawing).point2) } as Drawing;
        case "parallel-channel": case "triangle": case "fib-extension": case "pitchfork":
          return {
            ...d,
            point1: shift((d as ParallelChannelDrawing).point1),
            point2: shift((d as ParallelChannelDrawing).point2),
            point3: shift((d as ParallelChannelDrawing).point3),
          } as Drawing;
        default:
          return d;
      }
    },
    [],
  );

  // ── Move a single anchor point of a drawing to a new chart position ──
  const resizeDrawingAnchor = useCallback(
    (d: Drawing, anchorIndex: number, newPoint: ChartPoint): Drawing => {
      switch (d.tool) {
        case "horizontal":
          // anchor 0 and 1 both control the price
          return { ...d, price: newPoint.price } as Drawing;
        case "vertical":
          // anchor 0 and 1 both control the time
          return { ...d, time: newPoint.time } as Drawing;
        case "horizontal-ray":
        case "price-label":
        case "text":
          return { ...d, point: newPoint } as Drawing;
        case "trendline": case "ray": case "extended-line":
        case "rectangle": case "ellipse": case "arrow": case "measure":
        case "fibonacci": case "fib-fan": case "fib-timezone": case "fib-arc": case "gann-fan": {
          if (anchorIndex === 0) return { ...d, point1: newPoint } as Drawing;
          return { ...d, point2: newPoint } as Drawing;
        }
        case "parallel-channel": case "triangle": case "fib-extension": case "pitchfork": {
          if (anchorIndex === 0) return { ...d, point1: newPoint } as Drawing;
          if (anchorIndex === 1) return { ...d, point2: newPoint } as Drawing;
          return { ...d, point3: newPoint } as Drawing;
        }
        default:
          return d;
      }
    },
    [],
  );

  // ── Start anchor resize on pointer down (mouse + touch) ─────
  const handleAnchorMouseDown = useCallback(
    (e: React.PointerEvent | React.MouseEvent, anchorIndex: number) => {
      if (!selectedDrawingId) return;
      const original = drawings.find(dr => dr.id === selectedDrawingId);
      if (!original) return;
      e.preventDefault();
      e.stopPropagation();
      resizeDragRef.current = {
        drawingId: selectedDrawingId,
        anchorIndex,
        originalDrawing: JSON.parse(JSON.stringify(original)),
      };
      setIsResizing(true);
    },
    [selectedDrawingId, drawings],
  );

  // ── Global pointer move/up for anchor resizing (mouse + touch) ──
  useEffect(() => {
    if (!isResizing) return;

    const handleResizeMove = (e: PointerEvent) => {
      if (!resizeDragRef.current || !chart || !candleSeries) return;
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const chartPt = fromPixel(chart, candleSeries, px, py, istOffset);
      if (!chartPt) return;

      const moved = resizeDrawingAnchor(
        resizeDragRef.current.originalDrawing,
        resizeDragRef.current.anchorIndex,
        chartPt,
      );
      onDrawingUpdate(moved);
    };

    const handleResizeUp = () => {
      resizeDragRef.current = null;
      setIsResizing(false);
    };

    window.addEventListener("pointermove", handleResizeMove);
    window.addEventListener("pointerup", handleResizeUp);
    window.addEventListener("pointercancel", handleResizeUp);
    return () => {
      window.removeEventListener("pointermove", handleResizeMove);
      window.removeEventListener("pointerup", handleResizeUp);
      window.removeEventListener("pointercancel", handleResizeUp);
    };
  }, [isResizing, chart, candleSeries, istOffset, resizeDrawingAnchor, onDrawingUpdate]);

  // ── Manual double-tap detection (for touch devices where dblclick is unreliable) ──
  const lastTapRef = useRef<{ time: number; x: number; y: number } | null>(null);
  // Touch pointer tracking (ref so changes don't cost extra renders)
  const isTouchPointerRef = useRef(false);
  // Last pointer type used — read in handleClick to apply the lollipop Y-offset
  const lastPointerTypeRef = useRef<string>("mouse");

  const selectDrawingAtPixel = useCallback(
    (px: number, py: number, stopProp?: () => void) => {
      if (!chart || !candleSeries) return;
      setPendingPoints([]);
      setCursorPos(null);
      const hit = hitTestDrawing(px, py);
      if (hit) {
        if (stopProp) stopProp();
        setSelectedDrawingId(hit.id);
      } else {
        setSelectedDrawingId(null);
      }
    },
    [chart, candleSeries, hitTestDrawing],
  );

  // ── Double-click handler (mouse) — select drawing for move/delete ──
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      selectDrawingAtPixel(e.clientX - rect.left, e.clientY - rect.top, () => e.stopPropagation());
    },
    [selectDrawingAtPixel],
  );

  // ── Pointer down — start drag (if selected drawing under finger) OR start drawing ──
  const handlePointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      // Only primary pointer (left mouse or first touch)
      if (e.button !== 0 && e.pointerType === "mouse") return;
      if (isResizing) return;
      // Track pointer type for touch-specific adjustments
      lastPointerTypeRef.current = e.pointerType;
      isTouchPointerRef.current = e.pointerType === "touch";

      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;

      // ── Manual double-tap detection for touch ──
      if (e.pointerType === "touch") {
        const now = Date.now();
        const last = lastTapRef.current;
        if (last && now - last.time < 350 && Math.abs(px - last.x) < 20 && Math.abs(py - last.y) < 20) {
          // Double-tap detected
          lastTapRef.current = null;
          selectDrawingAtPixel(px, py, () => e.stopPropagation());
          return;
        }
        lastTapRef.current = { time: now, x: px, y: py };
      }

      // ── Start drag if a selected drawing is under the pointer ──
      if (!selectedDrawingId || !chart || !candleSeries) return;
      const hit = hitTestDrawing(px, py);
      if (!hit || hit.id !== selectedDrawingId) return;

      const chartPt = fromPixel(chart, candleSeries, px, py, istOffset);
      if (!chartPt) return;

      const original = drawings.find(d => d.id === selectedDrawingId);
      if (!original) return;

      e.preventDefault();
      e.stopPropagation();

      dragRef.current = {
        drawingId: selectedDrawingId,
        startX: px,
        startY: py,
        startPoint: chartPt,
        originalDrawing: JSON.parse(JSON.stringify(original)),
      };
      setIsDragging(true);
    },
    [selectedDrawingId, chart, candleSeries, istOffset, drawings, hitTestDrawing, isResizing, selectDrawingAtPixel],
  );

  // ── Global pointer move/up for dragging (mouse + touch) ────
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMove = (e: PointerEvent) => {
      if (!dragRef.current || !chart || !candleSeries) return;
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const chartPt = fromPixel(chart, candleSeries, px, py, istOffset);
      if (!chartPt) return;

      const dTime = chartPt.time - dragRef.current.startPoint.time;
      const dPrice = chartPt.price - dragRef.current.startPoint.price;
      const moved = offsetDrawing(dragRef.current.originalDrawing, dTime, dPrice);
      onDrawingUpdate(moved);
    };

    const handleGlobalUp = () => {
      dragRef.current = null;
      setIsDragging(false);
    };

    window.addEventListener("pointermove", handleGlobalMove);
    window.addEventListener("pointerup", handleGlobalUp);
    window.addEventListener("pointercancel", handleGlobalUp);
    return () => {
      window.removeEventListener("pointermove", handleGlobalMove);
      window.removeEventListener("pointerup", handleGlobalUp);
      window.removeEventListener("pointercancel", handleGlobalUp);
    };
  }, [isDragging, chart, candleSeries, istOffset, offsetDrawing, onDrawingUpdate]);

  // ── Click handler — multi-point state machine ───────────────
  const handleClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (activeTool === "cursor" || !chart || !candleSeries) return;

      // Ignore 2nd click of a double-click (browser fires click→click→dblclick)
      if (e.detail >= 2) return;

      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = e.clientX - rect.left;
      const rawY = e.clientY - rect.top;
      const isTouch = lastPointerTypeRef.current === "touch";

      // Hit-test at raw finger position for existing drawings
      const hitDrawing = hitTestDrawing(x, rawY);
      if (hitDrawing) {
        // Touch: single-tap selects the drawing (action bar handles delete/done)
        if (isTouch) {
          setSelectedDrawingId(hitDrawing.id);
          setLineEditor(null);
          setTrendlineEditor(null);
          setFibonacciEditor(null);
        }
        return;
      }

      // Deselect any previously selected drawing when starting a new one
      if (selectedDrawingId) {
        setSelectedDrawingId(null);
        setLineEditor(null);
        setTrendlineEditor(null);
        setFibonacciEditor(null);
      }

      // Touch: offset point above the finger so the lollipop preview aligns with placement
      const y = isTouch ? rawY - TOUCH_OFFSET_Y : rawY;
      const point = fromPixel(chart, candleSeries, x, y, istOffset);
      if (!point) return;

      const required = getRequiredClicks(activeTool);
      if (required === 0) return;

      const newPending = [...pendingPoints, point];

      if (newPending.length < required) {
        setPendingPoints(newPending);
        return;
      }

      // Enough points collected — create the drawing
      const lw = getDefaultLineWidth(activeTool);
      let drawing: Drawing;

      switch (activeTool) {
        case "horizontal":
          drawing = { id: nextDrawingId(), tool: "horizontal", price: newPending[0].price, color: activeColor, lineWidth: lw, visible: true };
          break;
        case "vertical":
          drawing = { id: nextDrawingId(), tool: "vertical", time: newPending[0].time, color: activeColor, lineWidth: lw, visible: true };
          break;
        case "horizontal-ray":
          drawing = { id: nextDrawingId(), tool: "horizontal-ray", point: newPending[0], color: activeColor, lineWidth: lw, visible: true };
          break;
        case "price-label":
          drawing = { id: nextDrawingId(), tool: "price-label", point: newPending[0], color: activeColor, lineWidth: lw, visible: true };
          break;
        case "text":
          // Show text input instead of creating drawing immediately
          setTextInput({ x, y, time: newPending[0].time, price: newPending[0].price });
          setTextInputValue("");
          setPendingPoints([]);
          setCursorPos(null);
          return;
        case "trendline":
          drawing = { id: nextDrawingId(), tool: "trendline", point1: newPending[0], point2: newPending[1], ray: true, color: activeColor, lineWidth: lw, visible: true };
          break;
        case "ray":
          drawing = { id: nextDrawingId(), tool: "ray", point1: newPending[0], point2: newPending[1], color: activeColor, lineWidth: lw, visible: true };
          break;
        case "extended-line":
          drawing = { id: nextDrawingId(), tool: "extended-line", point1: newPending[0], point2: newPending[1], color: activeColor, lineWidth: lw, visible: true };
          break;
        case "fibonacci":
          drawing = { id: nextDrawingId(), tool: "fibonacci", point1: newPending[0], point2: newPending[1], levels: [...DEFAULT_FIB_LEVELS], color: activeColor, lineWidth: lw, visible: true };
          break;
        case "rectangle":
          drawing = { id: nextDrawingId(), tool: "rectangle", point1: newPending[0], point2: newPending[1], color: activeColor, lineWidth: lw, visible: true, fillColor: activeColor, fillOpacity: 0.1 };
          break;
        case "ellipse":
          drawing = { id: nextDrawingId(), tool: "ellipse", point1: newPending[0], point2: newPending[1], color: activeColor, lineWidth: lw, visible: true, fillColor: activeColor, fillOpacity: 0.1 };
          break;
        case "arrow":
          drawing = { id: nextDrawingId(), tool: "arrow", point1: newPending[0], point2: newPending[1], color: activeColor, lineWidth: lw, visible: true };
          break;
        case "measure":
          drawing = { id: nextDrawingId(), tool: "measure", point1: newPending[0], point2: newPending[1], color: activeColor, lineWidth: 1, visible: true };
          break;
        case "fib-fan":
          drawing = { id: nextDrawingId(), tool: "fib-fan", point1: newPending[0], point2: newPending[1], color: activeColor, lineWidth: lw, visible: true };
          break;
        case "fib-timezone":
          drawing = { id: nextDrawingId(), tool: "fib-timezone", point1: newPending[0], point2: newPending[1], color: activeColor, lineWidth: lw, visible: true };
          break;
        case "fib-arc":
          drawing = { id: nextDrawingId(), tool: "fib-arc", point1: newPending[0], point2: newPending[1], color: activeColor, lineWidth: lw, visible: true };
          break;
        case "gann-fan":
          drawing = { id: nextDrawingId(), tool: "gann-fan", point1: newPending[0], point2: newPending[1], color: activeColor, lineWidth: lw, visible: true };
          break;
        case "parallel-channel":
          drawing = { id: nextDrawingId(), tool: "parallel-channel", point1: newPending[0], point2: newPending[1], point3: newPending[2], color: activeColor, lineWidth: lw, visible: true, fillColor: activeColor, fillOpacity: 0.05 };
          break;
        case "fib-extension":
          drawing = { id: nextDrawingId(), tool: "fib-extension", point1: newPending[0], point2: newPending[1], point3: newPending[2], levels: [...DEFAULT_FIB_EXT_LEVELS], color: activeColor, lineWidth: lw, visible: true };
          break;
        case "triangle":
          drawing = { id: nextDrawingId(), tool: "triangle", point1: newPending[0], point2: newPending[1], point3: newPending[2], color: activeColor, lineWidth: lw, visible: true, fillColor: activeColor, fillOpacity: 0.08 };
          break;
        case "pitchfork":
          drawing = { id: nextDrawingId(), tool: "pitchfork", point1: newPending[0], point2: newPending[1], point3: newPending[2], color: activeColor, lineWidth: lw, visible: true };
          break;
        default:
          setPendingPoints([]);
          return;
      }

      onDrawingComplete(drawing);
      setPendingPoints([]);
      setCursorPos(null);
    },
    [activeTool, activeColor, chart, candleSeries, istOffset, pendingPoints, onDrawingComplete, hitTestDrawing, selectedDrawingId],
  );

  // ── Pointer move for live preview (mouse + touch) ───────────
  const handlePointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (isDragging || isResizing) return; // drag/resize handled globally
      const isTouch = e.pointerType === "touch";
      isTouchPointerRef.current = isTouch;
      if (activeTool === "cursor" || pendingPoints.length === 0) return;
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      // On touch: float the preview crosshair above the finger (lollipop UX)
      const offsetY = isTouch ? TOUCH_OFFSET_Y : 0;
      setCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top - offsetY });
    },
    [activeTool, pendingPoints.length, isDragging, isResizing],
  );

  // ── Text input handlers ─────────────────────────────────────
  const commitTextInput = useCallback(() => {
    if (textInput && textInputValue.trim()) {
      const drawing: TextDrawing = {
        id: nextDrawingId(),
        tool: "text",
        point: { time: textInput.time, price: textInput.price },
        text: textInputValue.trim(),
        fontSize: 14,
        color: activeColor,
        lineWidth: 1,
        visible: true,
      };
      onDrawingComplete(drawing);
    }
    setTextInput(null);
    setTextInputValue("");
  }, [textInput, textInputValue, activeColor, onDrawingComplete]);

  // ═══════════════════════════════════════════════════════════════
  // RENDER: individual drawing types
  // ═══════════════════════════════════════════════════════════════
  const renderDrawing = (d: Drawing): React.ReactNode => {
    if (!d.visible || !chart || !candleSeries) return null;

    switch (d.tool) {
      // ── Horizontal line ─────────────────────────────────────
      case "horizontal": {
        const hd = d as HorizontalLineDrawing;
        const y = candleSeries.priceToCoordinate(hd.price);
        if (y === null) return null;
        return (
          <g key={d.id}>
            <line x1={0} y1={y} x2={svgSize.width} y2={y} stroke={hd.color} strokeWidth={hd.lineWidth} strokeDasharray="6 3" />
            <text x={svgSize.width - 5} y={y - 4} fill={hd.color} fontSize={11} textAnchor="end" fontFamily="monospace">{hd.price.toFixed(lineValuePrecision)}</text>
          </g>
        );
      }

      // ── Vertical line ───────────────────────────────────────
      case "vertical": {
        const vd = d as VerticalLineDrawing;
        const x = chart.timeScale().timeToCoordinate((vd.time + istOffset) as UTCTimestamp);
        if (x === null) return null;
        return (
          <g key={d.id}>
            <line x1={x} y1={0} x2={x} y2={svgSize.height} stroke={vd.color} strokeWidth={vd.lineWidth} strokeDasharray="6 3" />
          </g>
        );
      }

      // ── Horizontal ray ──────────────────────────────────────
      case "horizontal-ray": {
        const hrd = d as HorizontalRayDrawing;
        const p = toPixel(chart, candleSeries, hrd.point.time, hrd.point.price, istOffset);
        if (!p) return null;
        return (
          <g key={d.id}>
            <line x1={p.x} y1={p.y} x2={svgSize.width + 100} y2={p.y} stroke={hrd.color} strokeWidth={hrd.lineWidth} />
            <circle cx={p.x} cy={p.y} r={3} fill={hrd.color} />
            <text x={svgSize.width - 5} y={p.y - 4} fill={hrd.color} fontSize={11} textAnchor="end" fontFamily="monospace">{hrd.point.price.toFixed(lineValuePrecision)}</text>
          </g>
        );
      }

      // ── Trend line ──────────────────────────────────────────
      case "trendline": {
        const td = d as TrendlineDrawing;
        const p1 = toPixel(chart, candleSeries, td.point1.time, td.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, td.point2.time, td.point2.price, istOffset);
        if (!p1 || !p2) return null;
        const end = (td.ray ?? true) ? extendRay(p1, p2) : p2;
        return (
          <g key={d.id}>
            <line x1={p1.x} y1={p1.y} x2={end.x} y2={end.y} stroke={td.color} strokeWidth={td.lineWidth} />
            <circle cx={p1.x} cy={p1.y} r={3} fill={td.color} />
            <circle cx={p2.x} cy={p2.y} r={3} fill={td.color} />
          </g>
        );
      }

      // ── Ray ─────────────────────────────────────────────────
      case "ray": {
        const rd = d as RayDrawing;
        const p1 = toPixel(chart, candleSeries, rd.point1.time, rd.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, rd.point2.time, rd.point2.price, istOffset);
        if (!p1 || !p2) return null;
        const ep = extendRay(p1, p2);
        return (
          <g key={d.id}>
            <line x1={p1.x} y1={p1.y} x2={ep.x} y2={ep.y} stroke={rd.color} strokeWidth={rd.lineWidth} />
            <circle cx={p1.x} cy={p1.y} r={3} fill={rd.color} />
          </g>
        );
      }

      // ── Extended line ───────────────────────────────────────
      case "extended-line": {
        const eld = d as ExtendedLineDrawing;
        const p1 = toPixel(chart, candleSeries, eld.point1.time, eld.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, eld.point2.time, eld.point2.price, istOffset);
        if (!p1 || !p2) return null;
        const ext = extendBothWays(p1, p2);
        return (
          <g key={d.id}>
            <line x1={ext.start.x} y1={ext.start.y} x2={ext.end.x} y2={ext.end.y} stroke={eld.color} strokeWidth={eld.lineWidth} />
            <circle cx={p1.x} cy={p1.y} r={3} fill={eld.color} />
            <circle cx={p2.x} cy={p2.y} r={3} fill={eld.color} />
          </g>
        );
      }

      // ── Fibonacci retracement ───────────────────────────────
      case "fibonacci": {
        const fd = d as FibonacciDrawing;
        const p1 = toPixel(chart, candleSeries, fd.point1.time, fd.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, fd.point2.time, fd.point2.price, istOffset);
        if (!p1 || !p2) return null;
        const priceDiff = fd.point1.price - fd.point2.price;
        const minX = Math.min(p1.x, p2.x) - 20;
        const maxX = Math.max(p1.x, p2.x) + svgSize.width;
        const labelX = Math.max(28, svgSize.width - 54);
        return (
          <g key={d.id}>
            {fd.levels.map(level => {
              const price = fd.point2.price + priceDiff * level;
              const y = candleSeries.priceToCoordinate(price);
              if (y === null) return null;
              const opacity = level === 0 || level === 1 ? 0.8 : 0.5;
              return (
                <g key={`${d.id}-${level}`}>
                  <line x1={minX} y1={y} x2={maxX} y2={y} stroke={fd.color} strokeWidth={fd.lineWidth} strokeDasharray={level === 0.5 ? "4 2" : "6 3"} opacity={opacity} />
                  <text x={labelX} y={y - 3} fill={fd.color} fontSize={10} textAnchor="end" fontFamily="monospace" opacity={0.95}>
                    {(level * 100).toFixed(1)}% — {price.toFixed(2)}
                  </text>
                </g>
              );
            })}
            {/* Golden zone shading */}
            {(() => {
              const y382 = candleSeries.priceToCoordinate(fd.point2.price + priceDiff * 0.382);
              const y618 = candleSeries.priceToCoordinate(fd.point2.price + priceDiff * 0.618);
              if (y382 === null || y618 === null) return null;
              return <rect x={minX} y={Math.min(y382, y618)} width={maxX - minX} height={Math.abs(y618 - y382)} fill={fd.color} opacity={0.06} />;
            })()}
          </g>
        );
      }

      // ── Fibonacci extension ─────────────────────────────────
      case "fib-extension": {
        const fed = d as FibExtensionDrawing;
        const p1 = toPixel(chart, candleSeries, fed.point1.time, fed.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, fed.point2.time, fed.point2.price, istOffset);
        const p3 = toPixel(chart, candleSeries, fed.point3.time, fed.point3.price, istOffset);
        if (!p1 || !p2 || !p3) return null;
        const priceDiff = fed.point1.price - fed.point2.price;
        const labelX = Math.max(28, svgSize.width - 54);
        return (
          <g key={d.id}>
            <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={fed.color} strokeWidth={1} strokeDasharray="4 2" opacity={0.5} />
            <line x1={p2.x} y1={p2.y} x2={p3.x} y2={p3.y} stroke={fed.color} strokeWidth={1} strokeDasharray="4 2" opacity={0.5} />
            {fed.levels.map(level => {
              const price = fed.point3.price + priceDiff * level;
              const y = candleSeries.priceToCoordinate(price);
              if (y === null) return null;
              return (
                <g key={`${d.id}-ext-${level}`}>
                  <line x1={Math.min(p3.x, p1.x) - 20} y1={y} x2={svgSize.width} y2={y} stroke={fed.color} strokeWidth={1} strokeDasharray="6 3" opacity={0.6} />
                  <text x={labelX} y={y - 3} fill={fed.color} fontSize={10} textAnchor="end" fontFamily="monospace" opacity={0.95}>
                    {(level * 100).toFixed(1)}% — {price.toFixed(2)}
                  </text>
                </g>
              );
            })}
            <circle cx={p1.x} cy={p1.y} r={3} fill={fed.color} />
            <circle cx={p2.x} cy={p2.y} r={3} fill={fed.color} />
            <circle cx={p3.x} cy={p3.y} r={3} fill={fed.color} />
          </g>
        );
      }

      // ── Rectangle ───────────────────────────────────────────
      case "rectangle": {
        const rd = d as RectangleDrawing;
        const p1 = toPixel(chart, candleSeries, rd.point1.time, rd.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, rd.point2.time, rd.point2.price, istOffset);
        if (!p1 || !p2) return null;
        const x = Math.min(p1.x, p2.x);
        const y = Math.min(p1.y, p2.y);
        return (
          <g key={d.id}>
            <rect x={x} y={y} width={Math.abs(p2.x - p1.x)} height={Math.abs(p2.y - p1.y)} fill={rd.fillColor} fillOpacity={rd.fillOpacity} stroke={rd.color} strokeWidth={rd.lineWidth} />
          </g>
        );
      }

      // ── Ellipse ─────────────────────────────────────────────
      case "ellipse": {
        const ed = d as EllipseDrawing;
        const p1 = toPixel(chart, candleSeries, ed.point1.time, ed.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, ed.point2.time, ed.point2.price, istOffset);
        if (!p1 || !p2) return null;
        const cx = (p1.x + p2.x) / 2;
        const cy = (p1.y + p2.y) / 2;
        const rx = Math.abs(p2.x - p1.x) / 2;
        const ry = Math.abs(p2.y - p1.y) / 2;
        return (
          <g key={d.id}>
            <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill={ed.fillColor} fillOpacity={ed.fillOpacity} stroke={ed.color} strokeWidth={ed.lineWidth} />
          </g>
        );
      }

      // ── Triangle ────────────────────────────────────────────
      case "triangle": {
        const td = d as TriangleDrawing;
        const p1 = toPixel(chart, candleSeries, td.point1.time, td.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, td.point2.time, td.point2.price, istOffset);
        const p3 = toPixel(chart, candleSeries, td.point3.time, td.point3.price, istOffset);
        if (!p1 || !p2 || !p3) return null;
        return (
          <g key={d.id}>
            <polygon points={`${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y}`} fill={td.fillColor} fillOpacity={td.fillOpacity} stroke={td.color} strokeWidth={td.lineWidth} strokeLinejoin="round" />
            <circle cx={p1.x} cy={p1.y} r={3} fill={td.color} />
            <circle cx={p2.x} cy={p2.y} r={3} fill={td.color} />
            <circle cx={p3.x} cy={p3.y} r={3} fill={td.color} />
          </g>
        );
      }

      // ── Fibonacci Time Zones ───────────────────────────────
      case "fib-timezone": {
        const ftz = d as FibTimezoneDrawing;
        const p1 = toPixel(chart, candleSeries, ftz.point1.time, ftz.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, ftz.point2.time, ftz.point2.price, istOffset);
        if (!p1 || !p2) return null;
        const timeDiff = ftz.point2.time - ftz.point1.time;
        const fibSeq = [0, 1, 2, 3, 5, 8, 13, 21, 34];
        return (
          <g key={d.id}>
            {fibSeq.map((fib, idx) => {
              const time = ftz.point1.time + timeDiff * fib;
              const x = chart.timeScale().timeToCoordinate((time + istOffset) as UTCTimestamp);
              if (x === null) return null;
              const opacity = idx === 0 ? 0.8 : 0.5;
              return (
                <g key={`${d.id}-tz-${fib}`}>
                  <line x1={x} y1={0} x2={x} y2={svgSize.height} stroke={ftz.color} strokeWidth={ftz.lineWidth} strokeDasharray="6 3" opacity={opacity} />
                  <text x={x + 3} y={15} fill={ftz.color} fontSize={10} fontFamily="monospace" opacity={0.9}>
                    {fib}
                  </text>
                </g>
              );
            })}
            <circle cx={p1.x} cy={p1.y} r={3} fill={ftz.color} />
            <circle cx={p2.x} cy={p2.y} r={3} fill={ftz.color} />
          </g>
        );
      }

      // ── Fibonacci Fan ──────────────────────────────────────
      case "fib-fan": {
        const ff = d as FibFanDrawing;
        const p1 = toPixel(chart, candleSeries, ff.point1.time, ff.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, ff.point2.time, ff.point2.price, istOffset);
        if (!p1 || !p2) return null;
        const fibLevels = [0.236, 0.382, 0.5, 0.618, 0.786, 1];
        return (
          <g key={d.id}>
            {fibLevels.map(level => {
              const targetX = p1.x + (p2.x - p1.x);
              const targetY = p1.y + (p2.y - p1.y) * level;
              const ext = extendInOneDirection(p1, { x: targetX, y: targetY });
              const opacity = level === 0.5 ? 0.7 : 0.5;
              return (
                <g key={`${d.id}-fan-${level}`}>
                  <line x1={p1.x} y1={p1.y} x2={ext.x} y2={ext.y} stroke={ff.color} strokeWidth={ff.lineWidth} strokeDasharray={level === 0.5 ? "4 2" : "6 3"} opacity={opacity} />
                  <text x={p1.x + (targetX - p1.x) * 0.6} y={p1.y + (targetY - p1.y) * 0.6 - 5} fill={ff.color} fontSize={9} fontFamily="monospace" opacity={0.9}>
                    {(level * 100).toFixed(1)}%
                  </text>
                </g>
              );
            })}
            <circle cx={p1.x} cy={p1.y} r={3} fill={ff.color} />
            <circle cx={p2.x} cy={p2.y} r={3} fill={ff.color} />
          </g>
        );
      }

      // ── Gann Fan ───────────────────────────────────────────
      case "gann-fan": {
        const gf = d as GannFanDrawing;
        const p1 = toPixel(chart, candleSeries, gf.point1.time, gf.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, gf.point2.time, gf.point2.price, istOffset);
        if (!p1 || !p2) return null;
        // Gann angles: 1x8, 1x4, 1x3, 1x2, 1x1, 2x1, 3x1, 4x1, 8x1
        const gannAngles = [1/8, 1/4, 1/3, 1/2, 1, 2, 3, 4, 8];
        const baseAngle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
        return (
          <g key={d.id}>
            {gannAngles.map(ratio => {
              const angle = Math.atan(Math.tan(baseAngle) * ratio);
              const length = 2000;
              const endX = p1.x + length * Math.cos(angle);
              const endY = p1.y + length * Math.sin(angle);
              const opacity = ratio === 1 ? 0.8 : 0.5;
              return (
                <g key={`${d.id}-gann-${ratio}`}>
                  <line x1={p1.x} y1={p1.y} x2={endX} y2={endY} stroke={gf.color} strokeWidth={gf.lineWidth} strokeDasharray={ratio === 1 ? "none" : "6 3"} opacity={opacity} />
                  <text x={p1.x + 100 * Math.cos(angle)} y={p1.y + 100 * Math.sin(angle) - 5} fill={gf.color} fontSize={9} fontFamily="monospace" opacity={0.9}>
                    {ratio < 1 ? `1x${Math.round(1/ratio)}` : `${Math.round(ratio)}x1`}
                  </text>
                </g>
              );
            })}
            <circle cx={p1.x} cy={p1.y} r={3} fill={gf.color} />
            <circle cx={p2.x} cy={p2.y} r={3} fill={gf.color} />
          </g>
        );
      }

      // ── Fibonacci Arc ──────────────────────────────────────
      case "fib-arc": {
        const fa = d as FibArcDrawing;
        const p1 = toPixel(chart, candleSeries, fa.point1.time, fa.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, fa.point2.time, fa.point2.price, istOffset);
        if (!p1 || !p2) return null;
        const priceDiff = Math.abs(fa.point1.price - fa.point2.price);
        const fibLevels = [0.236, 0.382, 0.5, 0.618, 0.786, 1];
        return (
          <g key={d.id}>
            {fibLevels.map(level => {
              const radius = priceDiff * level;
              const radiusPixels = Math.abs(candleSeries.priceToCoordinate(fa.point1.price)! - candleSeries.priceToCoordinate(fa.point1.price - radius)!);
              if (!radiusPixels || radiusPixels < 1) return null;
              const opacity = level === 0.5 ? 0.6 : 0.4;
              return (
                <g key={`${d.id}-arc-${level}`}>
                  <circle cx={p1.x} cy={p1.y} r={radiusPixels} fill="none" stroke={fa.color} strokeWidth={fa.lineWidth} strokeDasharray="6 3" opacity={opacity} />
                  <text x={p1.x + radiusPixels * 0.7} y={p1.y - radiusPixels * 0.7} fill={fa.color} fontSize={9} fontFamily="monospace" opacity={0.9}>
                    {(level * 100).toFixed(1)}%
                  </text>
                </g>
              );
            })}
            <circle cx={p1.x} cy={p1.y} r={3} fill={fa.color} />
            <circle cx={p2.x} cy={p2.y} r={3} fill={fa.color} />
          </g>
        );
      }

      // ── Andrews' Pitchfork ─────────────────────────────────
      case "pitchfork": {
        const pf = d as PitchforkDrawing;
        const p1 = toPixel(chart, candleSeries, pf.point1.time, pf.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, pf.point2.time, pf.point2.price, istOffset);
        const p3 = toPixel(chart, candleSeries, pf.point3.time, pf.point3.price, istOffset);
        if (!p1 || !p2 || !p3) return null;
        // Median line from p1 to midpoint of p2-p3
        const midX = (p2.x + p3.x) / 2;
        const midY = (p2.y + p3.y) / 2;
        const medianExt = extendInOneDirection(p1, { x: midX, y: midY });
        // Upper line parallel to median through p2
        const dx = medianExt.x - p1.x;
        const dy = medianExt.y - p1.y;
        const upperExt = { x: p2.x + dx, y: p2.y + dy };
        // Lower line parallel to median through p3
        const lowerExt = { x: p3.x + dx, y: p3.y + dy };
        return (
          <g key={d.id}>
            {/* Median line */}
            <line x1={p1.x} y1={p1.y} x2={medianExt.x} y2={medianExt.y} stroke={pf.color} strokeWidth={pf.lineWidth} strokeDasharray="none" opacity={0.8} />
            {/* Upper line */}
            <line x1={p2.x} y1={p2.y} x2={upperExt.x} y2={upperExt.y} stroke={pf.color} strokeWidth={pf.lineWidth} strokeDasharray="6 3" opacity={0.6} />
            {/* Lower line */}
            <line x1={p3.x} y1={p3.y} x2={lowerExt.x} y2={lowerExt.y} stroke={pf.color} strokeWidth={pf.lineWidth} strokeDasharray="6 3" opacity={0.6} />
            {/* Anchor points */}
            <circle cx={p1.x} cy={p1.y} r={3} fill={pf.color} />
            <circle cx={p2.x} cy={p2.y} r={3} fill={pf.color} />
            <circle cx={p3.x} cy={p3.y} r={3} fill={pf.color} />
          </g>
        );
      }

      // ── Parallel channel ────────────────────────────────────
      case "parallel-channel": {
        const pcd = d as ParallelChannelDrawing;
        const p1 = toPixel(chart, candleSeries, pcd.point1.time, pcd.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, pcd.point2.time, pcd.point2.price, istOffset);
        const p3 = toPixel(chart, candleSeries, pcd.point3.time, pcd.point3.price, istOffset);
        if (!p1 || !p2 || !p3) return null;
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < 1) return null;
        const nx = -dy / len;
        const ny = dx / len;
        const dist = (p3.x - p1.x) * nx + (p3.y - p1.y) * ny;
        const ox = nx * dist;
        const oy = ny * dist;
        const q1 = { x: p1.x + ox, y: p1.y + oy };
        const q2 = { x: p2.x + ox, y: p2.y + oy };
        return (
          <g key={d.id}>
            <polygon points={`${p1.x},${p1.y} ${p2.x},${p2.y} ${q2.x},${q2.y} ${q1.x},${q1.y}`} fill={pcd.fillColor} fillOpacity={pcd.fillOpacity} />
            <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={pcd.color} strokeWidth={pcd.lineWidth} />
            <line x1={q1.x} y1={q1.y} x2={q2.x} y2={q2.y} stroke={pcd.color} strokeWidth={pcd.lineWidth} />
            <circle cx={p1.x} cy={p1.y} r={3} fill={pcd.color} />
            <circle cx={p2.x} cy={p2.y} r={3} fill={pcd.color} />
            <circle cx={p3.x} cy={p3.y} r={3} fill={pcd.color} opacity={0.6} />
          </g>
        );
      }

      // ── Text ────────────────────────────────────────────────
      case "text": {
        const txtd = d as TextDrawing;
        const p = toPixel(chart, candleSeries, txtd.point.time, txtd.point.price, istOffset);
        if (!p) return null;
        return (
          <g key={d.id}>
            <text x={p.x} y={p.y} fill={txtd.color} fontSize={txtd.fontSize} fontFamily="Arial, sans-serif" dominantBaseline="central">
              {txtd.text}
            </text>
          </g>
        );
      }

      // ── Arrow ───────────────────────────────────────────────
      case "arrow": {
        const ad = d as ArrowDrawing;
        const p1 = toPixel(chart, candleSeries, ad.point1.time, ad.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, ad.point2.time, ad.point2.price, istOffset);
        if (!p1 || !p2) return null;
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < 2) return null;
        const ux = dx / len;
        const uy = dy / len;
        const headLen = 14;
        const headW = 6;
        const tipL = { x: p2.x - ux * headLen + uy * headW, y: p2.y - uy * headLen - ux * headW };
        const tipR = { x: p2.x - ux * headLen - uy * headW, y: p2.y - uy * headLen + ux * headW };
        return (
          <g key={d.id}>
            <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={ad.color} strokeWidth={ad.lineWidth} />
            <polygon points={`${p2.x},${p2.y} ${tipL.x},${tipL.y} ${tipR.x},${tipR.y}`} fill={ad.color} />
            <circle cx={p1.x} cy={p1.y} r={3} fill={ad.color} />
          </g>
        );
      }

      // ── Price label ─────────────────────────────────────────
      case "price-label": {
        const pld = d as PriceLabelDrawing;
        const p = toPixel(chart, candleSeries, pld.point.time, pld.point.price, istOffset);
        if (!p) return null;
        const priceText = pld.point.price.toFixed(2);
        const tw = priceText.length * 8 + 16;
        return (
          <g key={d.id}>
            <rect x={p.x - tw / 2} y={p.y - 12} width={tw} height={24} rx={4} fill={pld.color} fillOpacity={0.9} />
            <text x={p.x} y={p.y + 1} fill="#ffffff" fontSize={12} textAnchor="middle" dominantBaseline="central" fontFamily="monospace" fontWeight="600">
              {priceText}
            </text>
          </g>
        );
      }

      // ── Measure ─────────────────────────────────────────────
      case "measure": {
        const md = d as MeasureDrawing;
        const p1 = toPixel(chart, candleSeries, md.point1.time, md.point1.price, istOffset);
        const p2 = toPixel(chart, candleSeries, md.point2.time, md.point2.price, istOffset);
        if (!p1 || !p2) return null;
        const priceDiff = md.point2.price - md.point1.price;
        const pctChange = md.point1.price !== 0 ? ((priceDiff / md.point1.price) * 100).toFixed(2) : "0.00";
        const isUp = priceDiff >= 0;
        const bgColor = isUp ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)";
        const textColor = isUp ? "#22c55e" : "#ef4444";
        const barCount = Math.round(Math.abs(md.point2.time - md.point1.time) / timeframeSeconds);
        const x = Math.min(p1.x, p2.x);
        const y = Math.min(p1.y, p2.y);
        const w = Math.abs(p2.x - p1.x);
        const h = Math.abs(p2.y - p1.y);
        const cx = (p1.x + p2.x) / 2;
        const cy = (p1.y + p2.y) / 2;
        return (
          <g key={d.id}>
            <rect x={x} y={y} width={w} height={h} fill={bgColor} stroke={textColor} strokeWidth={1} strokeDasharray="4 2" />
            <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={textColor} strokeWidth={1} strokeDasharray="4 2" />
            {/* Info overlay */}
            <rect x={cx - 65} y={cy - 20} width={130} height={40} rx={4} fill="rgba(0,0,0,0.85)" />
            <text x={cx} y={cy - 5} fill={textColor} fontSize={12} textAnchor="middle" fontFamily="monospace" fontWeight="600">
              {isUp ? "+" : ""}{priceDiff.toFixed(2)} ({pctChange}%)
            </text>
            <text x={cx} y={cy + 12} fill="#9ca3af" fontSize={10} textAnchor="middle" fontFamily="monospace">
              {barCount} bars
            </text>
          </g>
        );
      }

      default:
        return null;
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER: selection highlight — blue handles + glow on selected drawing
  // ═══════════════════════════════════════════════════════════════
  const renderSelectionHighlight = (): React.ReactNode => {
    if (!selectedDrawingId || !chart || !candleSeries) return null;
    const d = drawings.find(dr => dr.id === selectedDrawingId);
    if (!d || !d.visible) return null;
    const anchors = getDrawingAnchors(d);
    if (anchors.length === 0) return null;

    return (
      <g className="selection-highlights">
        {/* Anchor handles — interactive square blue selection handles */}
        {anchors.map((a, i) => (
          <g
            key={`sel-${d.id}-${i}`}
            className="anchor-handle"
            onPointerDown={(e) => { e.stopPropagation(); handleAnchorMouseDown(e as unknown as React.MouseEvent, i); }}
            style={{ cursor: "grab", touchAction: "none" }}
          >
            {/* Invisible larger hit area — touch gets 44×44 target */}
            <rect
              x={a.x - (isTouchDevice ? 22 : 10)} y={a.y - (isTouchDevice ? 22 : 10)}
              width={isTouchDevice ? 44 : 20} height={isTouchDevice ? 44 : 20}
              fill="transparent"
            />
            {/* Visible handle */}
            <rect
              x={a.x - (isTouchDevice ? 9 : 5)} y={a.y - (isTouchDevice ? 9 : 5)}
              width={isTouchDevice ? 18 : 10} height={isTouchDevice ? 18 : 10}
              fill="#2962ff" fillOpacity={0.9}
              stroke="#ffffff" strokeWidth={isTouchDevice ? 1.5 : 1}
              rx={isTouchDevice ? 4 : 2}
            />
          </g>
        ))}
        {/* Action buttons — top-right near first anchor */}
        {(() => {
          const ref = anchors[0];
          const spacing = isTouchDevice ? 34 : 24;
          const baseX = ref.x + (isTouchDevice ? 20 : 14);
          const by = ref.y - (isTouchDevice ? 20 : 14);
          const isHorizontal = d.tool === "horizontal";
          const isTrendline = d.tool === "trendline";
          const isFibonacci = d.tool === "fibonacci";

          const deleteX = baseX;
          const settingsX = baseX - spacing;

          return (
            <>
              {(isHorizontal || isTrendline || isFibonacci) && (
                <g
                  className={`settings-btn ${isHorizontal ? "line-settings-btn" : isTrendline ? "trendline-settings-btn" : "fibonacci-settings-btn"}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isHorizontal) {
                      openHorizontalLineEditor(d as HorizontalLineDrawing, settingsX, by);
                    } else if (isTrendline) {
                      openTrendlineEditor(d as TrendlineDrawing, settingsX, by);
                    } else if (isFibonacci) {
                      openFibonacciEditor(d as FibonacciDrawing, settingsX, by);
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <circle cx={settingsX} cy={by} r={isTouchDevice ? 13 : 9} fill="#2563eb" fillOpacity={0.95} stroke="#fff" strokeWidth={isTouchDevice ? 1.5 : 1} />
                  <path
                    d={isTouchDevice
                      ? `M ${settingsX - 3} ${by} a 3 3 0 1 0 6 0 a 3 3 0 1 0 -6 0 M ${settingsX} ${by - 6} v 2 M ${settingsX} ${by + 4} v 2 M ${settingsX - 6} ${by} h 2 M ${settingsX + 4} ${by} h 2`
                      : `M ${settingsX - 2.2} ${by} a 2.2 2.2 0 1 0 4.4 0 a 2.2 2.2 0 1 0 -4.4 0 M ${settingsX} ${by - 4.6} v 1.7 M ${settingsX} ${by + 2.9} v 1.7 M ${settingsX - 4.6} ${by} h 1.7 M ${settingsX + 2.9} ${by} h 1.7`}
                    stroke="#fff"
                    strokeWidth={isTouchDevice ? 1.6 : 1.35}
                    strokeLinecap="round"
                    fill="none"
                  />
                </g>
              )}
              <g
                className="delete-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onDrawingDelete(d.id);
                  setSelectedDrawingId(null);
                  setLineEditor(null);
                  setTrendlineEditor(null);
                  setFibonacciEditor(null);
                }}
                style={{ cursor: "pointer" }}
              >
                <circle cx={deleteX} cy={by} r={isTouchDevice ? 13 : 9} fill="#ef4444" fillOpacity={0.9} stroke="#fff" strokeWidth={isTouchDevice ? 1.5 : 1} />
                <line x1={deleteX - (isTouchDevice ? 4.5 : 3.5)} y1={by - (isTouchDevice ? 4.5 : 3.5)} x2={deleteX + (isTouchDevice ? 4.5 : 3.5)} y2={by + (isTouchDevice ? 4.5 : 3.5)} stroke="#fff" strokeWidth={isTouchDevice ? 2 : 1.5} strokeLinecap="round" />
                <line x1={deleteX + (isTouchDevice ? 4.5 : 3.5)} y1={by - (isTouchDevice ? 4.5 : 3.5)} x2={deleteX - (isTouchDevice ? 4.5 : 3.5)} y2={by + (isTouchDevice ? 4.5 : 3.5)} stroke="#fff" strokeWidth={isTouchDevice ? 2 : 1.5} strokeLinecap="round" />
              </g>
            </>
          );
        })()}
      </g>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER: live preview while placing points
  // ═══════════════════════════════════════════════════════════════
  const renderPreview = (): React.ReactNode => {
    if (!chart || !candleSeries) return null;

    // ── Always render placed pending-point anchors (numbered circles) ──────
    // These give the user immediate visual confirmation of where point 1 landed
    // even before they start sliding their finger for the live preview.
    const pendingAnchors = pendingPoints
      .map((pt, i) => {
        const p = toPixel(chart, candleSeries, pt.time, pt.price, istOffset);
        if (!p) return null;
        return (
          <g key={`pending-pt-${i}`}>
            <circle cx={p.x} cy={p.y} r={isTouchDevice ? 7 : 5} fill="#ffffff" stroke={activeColor} strokeWidth={isTouchDevice ? 2.5 : 2} opacity={0.92} />
            <text x={p.x} y={p.y + 1} fill={activeColor} fontSize={isTouchDevice ? 9 : 8} textAnchor="middle" dominantBaseline="central" fontWeight="bold" opacity={0.9}>{i + 1}</text>
          </g>
        );
      })
      .filter(Boolean) as React.ReactNode[];

    // ── Touch lollipop crosshair ─────────────────────────────────────────────
    // Floats 88 px above the finger so the user can see exactly where the
    // drawing point will be placed without the finger blocking the view.
    const showLollipop = isTouchDevice && isTouchPointerRef.current && cursorPos !== null && activeTool !== "cursor";
    const lollipop = showLollipop ? (
      <g className="touch-lollipop" style={{ pointerEvents: "none" }}>
        {/* Thin dashed stem from raw finger position up to the crosshair */}
        <line
          x1={cursorPos!.x} y1={cursorPos!.y + TOUCH_OFFSET_Y}
          x2={cursorPos!.x} y2={cursorPos!.y + 12}
          stroke={activeColor} strokeWidth={1.5} strokeDasharray="3 3" opacity={0.6}
        />
        {/* Outer glow ring */}
        <circle cx={cursorPos!.x} cy={cursorPos!.y} r={13} fill="rgba(255,255,255,0.05)" stroke={activeColor} strokeWidth={1} opacity={0.4} />
        {/* Crosshair background */}
        <circle cx={cursorPos!.x} cy={cursorPos!.y} r={9} fill="rgba(0,0,0,0.78)" stroke={activeColor} strokeWidth={2} />
        {/* Crosshair lines */}
        <line x1={cursorPos!.x - 17} y1={cursorPos!.y} x2={cursorPos!.x - 11} y2={cursorPos!.y} stroke={activeColor} strokeWidth={1.5} strokeLinecap="round" />
        <line x1={cursorPos!.x + 11} y1={cursorPos!.y} x2={cursorPos!.x + 17} y2={cursorPos!.y} stroke={activeColor} strokeWidth={1.5} strokeLinecap="round" />
        <line x1={cursorPos!.x} y1={cursorPos!.y - 17} x2={cursorPos!.x} y2={cursorPos!.y - 11} stroke={activeColor} strokeWidth={1.5} strokeLinecap="round" />
        <line x1={cursorPos!.x} y1={cursorPos!.y + 11} x2={cursorPos!.x} y2={cursorPos!.y + 17} stroke={activeColor} strokeWidth={1.5} strokeLinecap="round" />
      </g>
    ) : null;

    // If no cursor yet, just show anchors + lollipop (happens before first finger move)
    if (pendingPoints.length === 0 || !cursorPos) {
      return (pendingAnchors.length > 0 || lollipop) ? <>{pendingAnchors}{lollipop}</> : null;
    }

    const pixelPts = pendingPoints
      .map(p => toPixel(chart, candleSeries, p.time, p.price, istOffset))
      .filter(Boolean) as { x: number; y: number }[];
    if (pixelPts.length === 0) return <>{pendingAnchors}{lollipop}</>;

    const last = pixelPts[pixelPts.length - 1];

    let toolPreview: React.ReactNode = null;
    switch (activeTool) {
      // ── 2-click line tools ──────────────────────────────────
      case "trendline":
      case "ray":
      case "extended-line":
      case "arrow":
        toolPreview = <line x1={last.x} y1={last.y} x2={cursorPos.x} y2={cursorPos.y} stroke={activeColor} strokeWidth={2} strokeDasharray="4 4" opacity={0.7} />;
        break;

      case "horizontal-ray":
        toolPreview = (
          <>
            <circle cx={cursorPos.x} cy={cursorPos.y} r={3} fill={activeColor} opacity={0.7} />
            <line x1={cursorPos.x} y1={cursorPos.y} x2={svgSize.width + 100} y2={cursorPos.y} stroke={activeColor} strokeWidth={1} strokeDasharray="4 4" opacity={0.5} />
          </>
        );
        break;

      // ── Fibonacci / measure preview ─────────────────────────
      case "fibonacci":
      case "measure":
        toolPreview = (
          <>
            <line x1={0} y1={last.y} x2={svgSize.width} y2={last.y} stroke={activeColor} strokeWidth={1} strokeDasharray="2 2" opacity={0.4} />
            <line x1={0} y1={cursorPos.y} x2={svgSize.width} y2={cursorPos.y} stroke={activeColor} strokeWidth={1} strokeDasharray="2 2" opacity={0.4} />
          </>
        );
        break;

      // ── Rectangle / Ellipse preview ─────────────────────────
      case "rectangle":
      case "ellipse": {
        const rx = Math.min(last.x, cursorPos.x);
        const ry = Math.min(last.y, cursorPos.y);
        const rw = Math.abs(cursorPos.x - last.x);
        const rh = Math.abs(cursorPos.y - last.y);
        toolPreview = activeTool === "rectangle"
          ? <rect x={rx} y={ry} width={rw} height={rh} fill={activeColor} fillOpacity={0.08} stroke={activeColor} strokeWidth={1} strokeDasharray="4 4" opacity={0.7} />
          : <ellipse cx={(last.x + cursorPos.x) / 2} cy={(last.y + cursorPos.y) / 2} rx={rw / 2} ry={rh / 2} fill={activeColor} fillOpacity={0.08} stroke={activeColor} strokeWidth={1} strokeDasharray="4 4" opacity={0.7} />;
        break;
      }

      // ── Triangle preview (progressive) ──────────────────────
      case "triangle": {
        if (pendingPoints.length === 1) {
          toolPreview = <line x1={last.x} y1={last.y} x2={cursorPos.x} y2={cursorPos.y} stroke={activeColor} strokeWidth={1} strokeDasharray="4 4" opacity={0.7} />;
        } else if (pendingPoints.length === 2 && pixelPts.length >= 2) {
          const [pt1, pt2] = pixelPts;
          toolPreview = (
            <>
              <line x1={pt1.x} y1={pt1.y} x2={pt2.x} y2={pt2.y} stroke={activeColor} strokeWidth={1} opacity={0.7} />
              <line x1={pt2.x} y1={pt2.y} x2={cursorPos.x} y2={cursorPos.y} stroke={activeColor} strokeWidth={1} strokeDasharray="4 4" opacity={0.7} />
              <line x1={cursorPos.x} y1={cursorPos.y} x2={pt1.x} y2={pt1.y} stroke={activeColor} strokeWidth={1} strokeDasharray="4 4" opacity={0.7} />
            </>
          );
        }
        break;
      }

      // ── Parallel channel preview ────────────────────────────
      case "parallel-channel": {
        if (pendingPoints.length === 1) {
          toolPreview = <line x1={last.x} y1={last.y} x2={cursorPos.x} y2={cursorPos.y} stroke={activeColor} strokeWidth={1.5} strokeDasharray="4 4" opacity={0.7} />;
        } else if (pendingPoints.length === 2 && pixelPts.length >= 2) {
          const [pt1, pt2] = pixelPts;
          const dx = pt2.x - pt1.x;
          const dy = pt2.y - pt1.y;
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len >= 1) {
            const nx = -dy / len;
            const ny = dx / len;
            const dist = (cursorPos.x - pt1.x) * nx + (cursorPos.y - pt1.y) * ny;
            const ox = nx * dist;
            const oy = ny * dist;
            toolPreview = (
              <>
                <line x1={pt1.x} y1={pt1.y} x2={pt2.x} y2={pt2.y} stroke={activeColor} strokeWidth={1.5} opacity={0.7} />
                <line x1={pt1.x + ox} y1={pt1.y + oy} x2={pt2.x + ox} y2={pt2.y + oy} stroke={activeColor} strokeWidth={1} strokeDasharray="4 4" opacity={0.6} />
                <polygon points={`${pt1.x},${pt1.y} ${pt2.x},${pt2.y} ${pt2.x + ox},${pt2.y + oy} ${pt1.x + ox},${pt1.y + oy}`} fill={activeColor} fillOpacity={0.04} />
              </>
            );
          }
        }
        break;
      }

      // ── Fib extension preview ───────────────────────────────
      case "fib-extension": {
        if (pendingPoints.length === 1) {
          toolPreview = <line x1={last.x} y1={last.y} x2={cursorPos.x} y2={cursorPos.y} stroke={activeColor} strokeWidth={1} strokeDasharray="4 4" opacity={0.7} />;
        } else if (pendingPoints.length === 2 && pixelPts.length >= 2) {
          const [pt1, pt2] = pixelPts;
          toolPreview = (
            <>
              <line x1={pt1.x} y1={pt1.y} x2={pt2.x} y2={pt2.y} stroke={activeColor} strokeWidth={1} strokeDasharray="4 2" opacity={0.5} />
              <line x1={pt2.x} y1={pt2.y} x2={cursorPos.x} y2={cursorPos.y} stroke={activeColor} strokeWidth={1} strokeDasharray="4 4" opacity={0.7} />
            </>
          );
        }
        break;
      }

      default:
        break;
    }

    return <>{pendingAnchors}{toolPreview}{lollipop}</>;
  };

  const isDrawingMode = activeTool !== "cursor";
  const isCompactLayout = svgSize.width > 0 && svgSize.width <= 480;
  // SVG needs pointer-events when: drawing mode, or a drawing is selected (for drag/delete)
  const needsPointerEvents = isDrawingMode || selectedDrawingId !== null;

  return (
    <div ref={wrapperRef} className={`drawing-overlay-wrapper ${isCompactLayout ? "compact-layout" : ""}`}>
      <svg
        ref={svgRef}
        className={`drawing-overlay ${isDrawingMode ? "drawing-mode" : ""} ${selectedDrawingId ? "selected-mode" : ""} ${isDragging ? "dragging" : ""} ${isResizing ? "resizing" : ""}`}
        width={svgSize.width}
        height={svgSize.height}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        style={{
          pointerEvents: needsPointerEvents ? "all" : "none",
          touchAction: needsPointerEvents ? "none" : "auto",
        }}
      >
        {/* Completed drawings */}
        {drawingsVisible && drawings.map(d => renderDrawing(d))}
        {/* Selection highlight */}
        {renderSelectionHighlight()}
        {/* In-progress preview */}
        {renderPreview()}
      </svg>

      {/* Text input overlay */}
      {textInput && (
        <input
          ref={textInputRef}
          className="drawing-text-input"
          type="text"
          placeholder="Type text..."
          value={textInputValue}
          style={{ left: textInput.x, top: textInput.y - 16 }}
          onChange={e => setTextInputValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") { e.preventDefault(); commitTextInput(); }
            if (e.key === "Escape") { setTextInput(null); setTextInputValue(""); }
          }}
          onBlur={commitTextInput}
        />
      )}

      {/* Horizontal line value editor */}
      {lineEditor && (
        <div
          ref={lineEditorRef}
          className={`line-price-editor ${colorTheme}`}
          style={{ left: lineEditor.x, top: lineEditor.y }}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="line-price-editor__title">Edit Line Value</div>
          <div className="line-price-editor__input-row">
            <input
              type="number"
              step={lineInputStep}
              value={lineEditor.value}
              onChange={(e) =>
                setLineEditor((prev) =>
                  prev
                    ? {
                        ...prev,
                        value: e.target.value,
                        error: null,
                      }
                    : prev
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitHorizontalLineEditor();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setLineEditor(null);
                }
              }}
              autoFocus
            />
          </div>
          <div className="line-price-editor__hint">Tick size: {normalizedTick.toFixed(lineValuePrecision)}</div>
          {lineEditor.error && (
            <div className="line-price-editor__error">{lineEditor.error}</div>
          )}
          <div className="line-price-editor__actions">
            <button type="button" className="line-price-editor__btn line-price-editor__btn--ghost" onClick={() => setLineEditor(null)}>
              Cancel
            </button>
            <button type="button" className="line-price-editor__btn line-price-editor__btn--primary" onClick={submitHorizontalLineEditor}>
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Trendline parameters editor */}
      {trendlineEditor && (
        <div
          ref={trendlineEditorRef}
          className={`line-price-editor trendline-params-editor ${colorTheme}`}
          style={{ left: trendlineEditor.x, top: trendlineEditor.y }}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="line-price-editor__title">Trendline Parameters</div>
          <div className="trendline-params-grid">
            <label>
              <span>Time 1</span>
              <input
                type="text"
                placeholder="YYYY.MM.DD HH:mm"
                value={trendlineEditor.time1}
                onKeyDown={handleTrendlineEditorKeyDown}
                onChange={(e) =>
                  setTrendlineEditor((prev) =>
                    prev ? { ...prev, time1: e.target.value, error: null } : prev
                  )
                }
              />
            </label>
            <label>
              <span>Value 1</span>
              <input
                type="number"
                step={lineInputStep}
                value={trendlineEditor.value1}
                onKeyDown={handleTrendlineEditorKeyDown}
                onChange={(e) =>
                  setTrendlineEditor((prev) =>
                    prev ? { ...prev, value1: e.target.value, error: null } : prev
                  )
                }
              />
            </label>
            <label>
              <span>Time 2</span>
              <input
                type="text"
                placeholder="YYYY.MM.DD HH:mm"
                value={trendlineEditor.time2}
                onKeyDown={handleTrendlineEditorKeyDown}
                onChange={(e) =>
                  setTrendlineEditor((prev) =>
                    prev ? { ...prev, time2: e.target.value, error: null } : prev
                  )
                }
              />
            </label>
            <label>
              <span>Value 2</span>
              <input
                type="number"
                step={lineInputStep}
                value={trendlineEditor.value2}
                onKeyDown={handleTrendlineEditorKeyDown}
                onChange={(e) =>
                  setTrendlineEditor((prev) =>
                    prev ? { ...prev, value2: e.target.value, error: null } : prev
                  )
                }
              />
            </label>
          </div>

          <label className="trendline-params-ray">
            <input
              type="checkbox"
              checked={trendlineEditor.ray}
              onChange={(e) =>
                setTrendlineEditor((prev) =>
                  prev ? { ...prev, ray: e.target.checked } : prev
                )
              }
            />
            Ray (extend through point-2)
          </label>

          <div className="line-price-editor__hint">Date format: YYYY.MM.DD HH:mm | Tick: {normalizedTick.toFixed(lineValuePrecision)}</div>
          {trendlineEditor.error && <div className="line-price-editor__error">{trendlineEditor.error}</div>}

          <div className="line-price-editor__actions">
            <button type="button" className="line-price-editor__btn line-price-editor__btn--ghost" onClick={() => setTrendlineEditor(null)}>
              Cancel
            </button>
            <button type="button" className="line-price-editor__btn line-price-editor__btn--primary" onClick={submitTrendlineEditor}>
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Fibonacci retracement parameters editor */}
      {fibonacciEditor && (
        <div
          ref={fibonacciEditorRef}
          className={`line-price-editor fibonacci-params-editor ${colorTheme}`}
          style={{ left: fibonacciEditor.x, top: fibonacciEditor.y }}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="line-price-editor__title">Fibonacci Parameters</div>

          <div className="trendline-params-grid">
            <label>
              <span>Time 1</span>
              <input
                type="text"
                placeholder="YYYY.MM.DD HH:mm"
                value={fibonacciEditor.time1}
                onKeyDown={handleFibonacciEditorKeyDown}
                onChange={(e) =>
                  setFibonacciEditor((prev) =>
                    prev ? { ...prev, time1: e.target.value, error: null } : prev
                  )
                }
              />
            </label>
            <label>
              <span>Value 1</span>
              <input
                type="number"
                step={lineInputStep}
                value={fibonacciEditor.value1}
                onKeyDown={handleFibonacciEditorKeyDown}
                onChange={(e) =>
                  setFibonacciEditor((prev) =>
                    prev ? { ...prev, value1: e.target.value, error: null } : prev
                  )
                }
              />
            </label>
            <label>
              <span>Time 2</span>
              <input
                type="text"
                placeholder="YYYY.MM.DD HH:mm"
                value={fibonacciEditor.time2}
                onKeyDown={handleFibonacciEditorKeyDown}
                onChange={(e) =>
                  setFibonacciEditor((prev) =>
                    prev ? { ...prev, time2: e.target.value, error: null } : prev
                  )
                }
              />
            </label>
            <label>
              <span>Value 2</span>
              <input
                type="number"
                step={lineInputStep}
                value={fibonacciEditor.value2}
                onKeyDown={handleFibonacciEditorKeyDown}
                onChange={(e) =>
                  setFibonacciEditor((prev) =>
                    prev ? { ...prev, value2: e.target.value, error: null } : prev
                  )
                }
              />
            </label>
          </div>

          <div className="fibonacci-levels">
            <div className="fibonacci-levels__header">Fib Levels</div>
            <div className="fibonacci-levels__panel">
              <div className="fibonacci-levels__list">
                {fibonacciEditor.levels.map((lvl, idx) => (
                  <button
                    key={`${lvl}-${idx}`}
                    type="button"
                    className={`fibonacci-levels__item ${fibonacciEditor.selectedLevelIndex === idx ? "selected" : ""}`}
                    onClick={() => setFibonacciEditor((prev) => prev ? { ...prev, selectedLevelIndex: idx, error: null } : prev)}
                  >
                    <span>#{idx + 1}</span>
                    <span>{lvl.toFixed(3)}</span>
                    <span>{(lvl * 100).toFixed(1)}</span>
                  </button>
                ))}
              </div>
              <div className="fibonacci-levels__actions">
                <div className="fibonacci-levels__add-row">
                  <input
                    type="number"
                    step="0.001"
                    placeholder="e.g. 1.272"
                    value={fibonacciEditor.newLevelValue}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addFibonacciLevel();
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        setFibonacciEditor(null);
                      }
                    }}
                    onChange={(e) => setFibonacciEditor((prev) => prev ? { ...prev, newLevelValue: e.target.value, error: null } : prev)}
                  />
                  <button type="button" className="line-price-editor__btn line-price-editor__btn--primary" onClick={addFibonacciLevel}>Add</button>
                </div>
                <button type="button" className="line-price-editor__btn line-price-editor__btn--ghost" onClick={deleteFibonacciLevel}>Delete</button>
                <button type="button" className="line-price-editor__btn line-price-editor__btn--ghost" onClick={resetFibonacciLevels}>Defaults</button>
              </div>
            </div>
          </div>

          <div className="line-price-editor__hint">Date format: YYYY.MM.DD HH:mm | Tick: {normalizedTick.toFixed(lineValuePrecision)} | Ray: Not applicable for Fib Retracement</div>
          {fibonacciEditor.error && <div className="line-price-editor__error">{fibonacciEditor.error}</div>}

          <div className="line-price-editor__actions">
            <button type="button" className="line-price-editor__btn line-price-editor__btn--ghost" onClick={() => setFibonacciEditor(null)}>
              Cancel
            </button>
            <button type="button" className="line-price-editor__btn line-price-editor__btn--primary" onClick={submitFibonacciEditor}>
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Mobile: cancel in-progress drawing */}
      {isTouchDevice && pendingPoints.length > 0 && (
        <button
          className="mobile-drawing-cancel"
          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onClick={(e) => {
            e.stopPropagation();
            setPendingPoints([]);
            setCursorPos(null);
          }}
        >
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          Cancel
        </button>
      )}

      {/* Mobile: action bar when a drawing is selected */}
      {isTouchDevice && selectedDrawingId && pendingPoints.length === 0 && !lineEditor && !trendlineEditor && !fibonacciEditor && (
        <div className="mobile-drawing-actions">
          {(() => {
            const selected = drawings.find((dr) => dr.id === selectedDrawingId);
            if (!selected || (selected.tool !== "horizontal" && selected.tool !== "trendline" && selected.tool !== "fibonacci")) return null;
            return (
              <button
                className="mda-edit"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  if (selected.tool === "horizontal") {
                    openHorizontalLineEditor(selected as HorizontalLineDrawing, svgSize.width * 0.5, svgSize.height * 0.58);
                  } else if (selected.tool === "trendline") {
                    openTrendlineEditor(selected as TrendlineDrawing, svgSize.width * 0.56, svgSize.height * 0.54);
                  } else {
                    openFibonacciEditor(selected as FibonacciDrawing, svgSize.width * 0.58, svgSize.height * 0.5);
                  }
                }}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2.5l2.1.5.8 2 .1.2 1.8.9 2-.8 1.5 1.5-.8 2 .9 1.8.2.1 2 .8.5 2.1-.5 2.1-2 .8-.2.1-.9 1.8.8 2-1.5 1.5-2-.8-1.8.9-.1.2-.8 2-2.1.5-2.1-.5-.8-2-.1-.2-1.8-.9-2 .8-1.5-1.5.8-2-.9-1.8-.2-.1-2-.8L2.5 12l.5-2.1 2-.8.2-.1.9-1.8-.8-2L7.8 3.7l2 .8 1.8-.9.1-.2.8-2L12 2.5z" />
                  <circle cx="12" cy="12" r="2.7" />
                </svg>
                Edit
              </button>
            );
          })()}
          <button
            className="mda-delete"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onDrawingDelete(selectedDrawingId);
              setSelectedDrawingId(null);
              setLineEditor(null);
              setTrendlineEditor(null);
              setFibonacciEditor(null);
            }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3,6 5,6 21,6" />
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            </svg>
            Delete
          </button>
          <button
            className="mda-done"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); setSelectedDrawingId(null); setLineEditor(null); setTrendlineEditor(null); setFibonacciEditor(null); }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="20,6 9,17 4,12" />
            </svg>
            Done
          </button>
        </div>
      )}
    </div>
  );
};

export default DrawingOverlay;
